import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Icon,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import moment from "moment";
import axios from "util/Api";
import "../SpareStockTaking/style.css";
import "./style.css";

const { TabPane } = Tabs;
const { TextArea } = Input;

const toId = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === "object" && value._id) {
    return value._id.toString();
  }
  return value.toString();
};

const toName = (value) => {
  if (!value) {
    return "-";
  }
  if (typeof value === "object" && value.name) {
    return value.name;
  }
  return "-";
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const pickSubLocationRefs = (location) => {
  if (!location || typeof location !== "object") {
    return [];
  }
  const candidates = [
    location.subLocations,
    location.sublocations,
    location.subLocation,
    location.clients,
    location.client,
  ];
  for (let index = 0; index < candidates.length; index += 1) {
    const value = candidates[index];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
};

const selectFilterByChildren = (input, option) => {
  const source = (option && option.props && option.props.children) || "";
  const text = Array.isArray(source) ? source.join(" ") : source.toString();
  return text.toLowerCase().indexOf((input || "").toLowerCase()) >= 0;
};

class SparesReconciliation extends Component {
  state = {
    loadingList: false,
    loadingDetails: false,
    ignoreSaving: false,
    recheckClosing: false,
    bulkIgnoreSaving: false,
    locations: [],
    subLocationNameMap: {},
    vouchers: [],
    currentStockTaking: null,
    inventoryBySpare: {},
    selectedReconciliationRowKeys: [],
    selectedVoucherId: null,
    filters: {
      date: null,
      locationId: undefined,
    },
    ignoreModal: {
      visible: false,
      mode: "create", // create | view
      row: null,
      remark: "",
    },
    savModal: {
      visible: false,
      row: null,
    },
    bulkIgnoreModal: {
      visible: false,
      rows: [],
      remark: "",
    },
    adjustmentSaving: false,
    adjustmentModal: {
      visible: false,
      row: null,
      reason: "",
      addSubLocationId: undefined,
      allocations: [],
    },
    pendingRefModal: {
      visible: false,
      rows: [],
    },
  };

  rateSuggestionRequestId = 0;

  componentDidMount = async () => {
    await Promise.all([
      this.loadFilters(),
      this.loadSubmittedVouchers(),
    ]);

    const query = new URLSearchParams((this.props.location && this.props.location.search) || "");
    const sstIdFromQuery = query.get("sstId");
    const selectableVouchers = this.getFilteredVoucherOptions(this.state.filters, this.state.vouchers);
    const hasQueryVoucher = sstIdFromQuery
      && (this.state.vouchers || []).some((row) => row && toId(row._id) === sstIdFromQuery.toString());
    const firstSelectableVoucher = this.getPreferredVoucherId(selectableVouchers);
    const nextSelectedVoucher = hasQueryVoucher ? sstIdFromQuery.toString() : firstSelectableVoucher;

    if (nextSelectedVoucher) {
      await this.handleVoucherChange(nextSelectedVoucher);
    }
  };

  componentDidUpdate = async (prevProps) => {
    if (prevProps.authUser !== this.props.authUser) {
      this.loadFilters();
    }

    const prevSearch = (prevProps.location && prevProps.location.search) || "";
    const currentSearch = (this.props.location && this.props.location.search) || "";
    if (prevSearch === currentSearch) {
      return;
    }
    const query = new URLSearchParams(currentSearch);
    const sstIdFromQuery = query.get("sstId");
    if (!sstIdFromQuery) {
      return;
    }
    if (this.state.selectedVoucherId && this.state.selectedVoucherId.toString() === sstIdFromQuery.toString()) {
      return;
    }
    await this.handleVoucherChange(sstIdFromQuery.toString());
  };

  getErrorMessage = (error, fallbackMessage) => (
    (error && error.response && error.response.data && error.response.data.message)
    || fallbackMessage
  );

  buildReconciliationOverview = (updated) => {
    const records = Array.isArray(updated && updated.records) ? updated.records : [];
    const totalRecords = toNumber(updated && updated.totalRecords) || records.length;
    const pending = records.filter((row) => (((row && row.action) || "").toString().toUpperCase() === "PENDING")).length;
    const adjusted = records.filter((row) => (((row && row.action) || "").toString().toUpperCase() === "ADJUSTMENT")).length;
    const ignored = records.filter((row) => (((row && row.action) || "").toString().toUpperCase() === "IGNORE")).length;
    const reviewed = Math.max(0, totalRecords - pending);

    return {
      voucherNo: (updated && updated.voucherNo) || "-",
      status: (updated && updated.status) || "-",
      totalRecords,
      reviewed,
      pending,
      adjusted,
      ignored,
      completedAt: updated && updated.completedAt
        ? moment(updated.completedAt).format("DD MMM YYYY HH:mm")
        : null,
    };
  };

  redirectToSstQueue = () => {
    if (this.props.history && typeof this.props.history.push === "function") {
      this.props.history.push("/spare-stock-taking");
    }
  };

  openAdjustmentVoucherReport = () => {
    const { currentStockTaking } = this.state;
    const params = new URLSearchParams();
    if (currentStockTaking && currentStockTaking.stockTakingDate) {
      const reportDate = moment(currentStockTaking.stockTakingDate);
      if (reportDate.isValid()) {
        const normalized = reportDate.format("YYYY-MM-DD");
        params.set("fromDate", normalized);
        params.set("toDate", normalized);
      }
    }
    if (currentStockTaking && currentStockTaking.filters && currentStockTaking.filters.location) {
      params.set("location", currentStockTaking.filters.location.toString());
    }
    if (currentStockTaking && currentStockTaking.filters && currentStockTaking.filters.subLocation) {
      params.set("subLocation", currentStockTaking.filters.subLocation.toString());
    }
    const query = params.toString();
    this.props.history.push(`/spare-adjustment-vouchers${query ? `?${query}` : ""}`);
  };

  showReconciliationSuccessPopup = (updated, titleText) => {
    const overview = this.buildReconciliationOverview(updated);
    Modal.success({
      title: titleText || "Reconciliation Completed",
      okText: "Go to SST Queue",
      content: (
        <div>
          <div><strong>Voucher:</strong> {overview.voucherNo}</div>
          <div><strong>Status:</strong> {overview.status}</div>
          <div><strong>Total Spare Parts:</strong> {overview.totalRecords}</div>
          <div><strong>Reviewed:</strong> {overview.reviewed}</div>
          <div><strong>Pending:</strong> {overview.pending}</div>
          <div><strong>Adjusted:</strong> {overview.adjusted}</div>
          <div><strong>Ignored:</strong> {overview.ignored}</div>
          {overview.completedAt && <div><strong>Completed At:</strong> {overview.completedAt}</div>}
        </div>
      ),
      onOk: this.redirectToSstQueue,
    });
  };

  showReconciliationErrorPopup = (error, fallbackMessage, titleText) => {
    const errorMessage = this.getErrorMessage(error, fallbackMessage);
    Modal.error({
      title: titleText || "Reconciliation Update Failed",
      okText: "Close",
      content: (
        <div>
          <div>{errorMessage}</div>
        </div>
      ),
    });
  };

  hasPermission = (permission, moduleName = "SparesReconciliation") => {
    const authUser = this.props.authUser || {};
    const roles = authUser.roles || [];
    const permissions = roles
      .map((role) => (role && role.access) ? role.access : [])
      .flat(1)
      .filter((entry) => entry && entry.name === moduleName)
      .map((entry) => entry.permissions || [])
      .flat(1);
    return permissions.indexOf(permission) >= 0;
  };

  canTakeAction = () => (
    this.hasPermission("u")
    || this.hasPermission("c")
  );

  tooltipLabel = (label, tip) => (
    <span className="spares-tooltip-label">
      {label}
      <Tooltip title={tip}>
        <Icon type="info-circle" className="spares-tooltip-icon" />
      </Tooltip>
    </span>
  );

  loadFilters = async () => {
    const authUser = this.props.authUser || {};
    const rawLocations = Array.isArray(authUser.locations) ? authUser.locations : [];
    const rawClients = Array.isArray(authUser.clients) ? authUser.clients : [];

    const clientMap = {};
    rawClients.forEach((client) => {
      const clientId = toId(client && (client._id || client.id));
      if (!clientId) {
        return;
      }
      clientMap[clientId] = {
        _id: clientId,
        name: (client && client.name) || "-",
      };
    });

    const locations = rawLocations
      .map((location) => {
        const locationId = toId(location && (location._id || location.id));
        if (!locationId) {
          return null;
        }
        const rawSubLocations = pickSubLocationRefs(location);
        const subLocationMap = {};

        rawSubLocations.forEach((subLocation) => {
          const subLocationId = toId(subLocation && (subLocation._id || subLocation.id || subLocation));
          if (!subLocationId) {
            return;
          }
          const mapped = typeof subLocation === "object"
            ? subLocation
            : clientMap[subLocationId];
          subLocationMap[subLocationId] = {
            _id: subLocationId,
            name: (mapped && mapped.name) || ((clientMap[subLocationId] && clientMap[subLocationId].name) || "-"),
          };
        });

        return {
          ...location,
          _id: locationId,
          name: (location && location.name) || "-",
          subLocations: Object.values(subLocationMap),
        };
      })
      .filter((item) => !!item)
      .sort((a, b) => a.name.localeCompare(b.name));

    const subLocationNameMap = {};
    locations.forEach((location) => {
      (location.subLocations || []).forEach((subLocation) => {
        const subLocationId = toId(subLocation && (subLocation._id || subLocation.id));
        if (!subLocationId) {
          return;
        }
        subLocationNameMap[subLocationId] = (subLocation && subLocation.name) || "-";
      });
    });
    Object.keys(clientMap).forEach((clientId) => {
      if (!subLocationNameMap[clientId]) {
        subLocationNameMap[clientId] = clientMap[clientId].name;
      }
    });

    this.setState({
      locations,
      subLocationNameMap,
    });
  };

  loadSubmittedVouchers = async () => {
    this.setState({ loadingList: true });
    try {
      const response = await axios.get("/spare-stock-taking/reconcile/list?limit=500&offset=0&sortOrder=-1&statuses=SUBMITTED,CLOSED");
      const payload = (response && response.data) || {};
      this.setState({
        vouchers: payload.data || [],
      });
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to load reconciliation vouchers"));
    } finally {
      this.setState({ loadingList: false });
    }
  };

  getStockTakingLocationId = (stockTaking) => {
    if (!stockTaking || !stockTaking.filters) {
      return null;
    }
    return toId(stockTaking.filters.location);
  };

  getStockTakingSpareIds = (stockTaking) => {
    const records = (stockTaking && stockTaking.records) || [];
    const spareIdMap = {};
    records.forEach((row) => {
      const spareId = toId(row && row.spare);
      if (spareId) {
        spareIdMap[spareId] = true;
      }
    });
    return Object.keys(spareIdMap);
  };

  getSubLocationNameById = (subLocationId) => {
    if (!subLocationId) {
      return "-";
    }
    const subLocationNameMap = this.state.subLocationNameMap || {};
    const key = subLocationId.toString();
    if (subLocationNameMap[key]) {
      return subLocationNameMap[key];
    }
    return "-";
  };

  fetchInventoryBySpare = async (stockTaking) => {
    if (!stockTaking) {
      return {};
    }

    const selectedToDate = stockTaking.stockTakingDate
      ? moment(stockTaking.stockTakingDate).format("YYYY-MM-DD")
      : null;
    const locationId = this.getStockTakingLocationId(stockTaking);
    const subLocationId = stockTaking && stockTaking.filters
      ? toId(stockTaking.filters.subLocation)
      : null;
    if (!selectedToDate || !locationId) {
      return {};
    }

    const payload = {
      selectedFromDate: selectedToDate,
      selectedToDate,
      selectedLocation: [locationId],
      selectedSubLocation: subLocationId ? [subLocationId] : [],
      selectedSparePart: [],
      selectedSparePartType: [],
      showOpening: true,
      showClosing: true,
      showInward: true,
      showOutward: true,
      rateCalculateFormula: "LIFO",
    };

    try {
      const response = await axios.post("/common/report/spare-inventory", payload);
      const rows = (response && response.data) || [];
      const aggregateBySpare = {};

      (rows || []).forEach((row) => {
        const spareId = toId((row && row.sparepart && row.sparepart._id) || (row && row._id));
        if (!spareId) {
          return;
        }

        if (!aggregateBySpare[spareId]) {
          aggregateBySpare[spareId] = {
            fallbackCloseQty: 0,
            subLocationQtyMap: {},
            code: "-",
            name: "-",
            category: "-",
            uom: "-",
          };
        }
        const inventorySpare = (row && row.sparepart) || {};
        const inventorySpareType = (row && row.sparepartType) || {};
        const resolvedCode = (
          (inventorySpare && (inventorySpare.code || inventorySpare.spareCode || inventorySpare.partCode))
          || (row && (row.spareCode || row.code))
          || "-"
        );
        const resolvedName = (
          (inventorySpare && inventorySpare.name)
          || (row && row.spareName)
          || "-"
        );
        const resolvedCategory = (
          (inventorySpareType && inventorySpareType.name)
          || (row && (row.spareTypeName || row.sparePartType))
          || "-"
        );
        const resolvedUom = (
          (row && (row.unit || row.unitName))
          || "-"
        );
        if (aggregateBySpare[spareId].code === "-" && resolvedCode && resolvedCode !== "-") {
          aggregateBySpare[spareId].code = resolvedCode;
        }
        if (aggregateBySpare[spareId].name === "-" && resolvedName && resolvedName !== "-") {
          aggregateBySpare[spareId].name = resolvedName;
        }
        if (aggregateBySpare[spareId].category === "-" && resolvedCategory && resolvedCategory !== "-") {
          aggregateBySpare[spareId].category = resolvedCategory;
        }
        if (aggregateBySpare[spareId].uom === "-" && resolvedUom && resolvedUom !== "-") {
          aggregateBySpare[spareId].uom = resolvedUom;
        }

        const fallbackCloseQty = toNumber(
          (row && row.closeQty)
          || (row && row.close_quantity)
          || 0,
        );
        aggregateBySpare[spareId].fallbackCloseQty += fallbackCloseQty;

        const inventoryEntries = (row && Array.isArray(row.inventory)) ? row.inventory : [];
        inventoryEntries.forEach((entry) => {
          const direction = (entry && entry.direction) ? entry.direction.toString().toLowerCase() : "";
          if (direction !== "closing") {
            return;
          }
          const subLocationId = toId(entry && entry.subLocation) || "__none__";
          const closeQty = toNumber(
            (entry && entry.close_quantity)
            || (entry && entry.closeQty)
            || 0,
          );
          aggregateBySpare[spareId].subLocationQtyMap[subLocationId] = toNumber(
            aggregateBySpare[spareId].subLocationQtyMap[subLocationId],
          ) + closeQty;
        });
      });

      const inventoryBySpare = {};
      Object.keys(aggregateBySpare).forEach((spareId) => {
        const item = aggregateBySpare[spareId];
        const subLocationQtyMap = item.subLocationQtyMap || {};
        const subLocationDetails = Object.keys(subLocationQtyMap).map((subLocationId) => ({
          id: subLocationId,
          name: subLocationId === "__none__" ? "-" : this.getSubLocationNameById(subLocationId),
          qty: toNumber(subLocationQtyMap[subLocationId]),
        }));
        const footprintSubLocationDetails = subLocationDetails.filter(
          (entry) => toNumber(entry && entry.qty) !== 0,
        );
        const inventoryQtyFromSubLocations = subLocationDetails.reduce(
          (sum, entry) => sum + toNumber(entry.qty),
          0,
        );
        const inventoryQty = subLocationDetails.length
          ? inventoryQtyFromSubLocations
          : toNumber(item.fallbackCloseQty);
        const locationFootprint = footprintSubLocationDetails.length
          ? footprintSubLocationDetails.map((entry) => `${entry.name} (${entry.qty})`).join(", ")
          : (inventoryQty !== 0 ? `- (${inventoryQty})` : "Not found in inventory");

        inventoryBySpare[spareId] = {
          code: item.code || "-",
          name: item.name || "-",
          category: item.category || "-",
          uom: item.uom || "-",
          quantity: inventoryQty,
          locationFootprint,
          allSubLocationDetails: subLocationDetails,
          subLocationDetails: footprintSubLocationDetails,
        };
      });

      return inventoryBySpare;
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to load inventory for reconciliation"));
      return {};
    }
  };

  openStockTaking = async (id) => {
    if (!id) {
      this.setState({
        currentStockTaking: null,
        inventoryBySpare: {},
      });
      return;
    }
    this.setState({ loadingDetails: true });
    try {
      const response = await axios.get(`/spare-stock-taking/reconcile/${id}`);
      const data = (response && response.data) || null;
      const inventoryBySpare = await this.fetchInventoryBySpare(data);
      this.setState({
        currentStockTaking: data,
        inventoryBySpare,
      });
    } catch (error) {
      this.setState({
        currentStockTaking: null,
        inventoryBySpare: {},
      });
      message.error(this.getErrorMessage(error, "Failed to load reconciliation details"));
    } finally {
      this.setState({ loadingDetails: false });
    }
  };

  getFilteredVoucherOptions = (filters = this.state.filters, vouchers = this.state.vouchers) => {
    const currentFilters = filters || {};
    const sourceRows = vouchers || [];

    return sourceRows.filter((row) => {
      const rowDate = row && row.stockTakingDate ? moment(row.stockTakingDate).format("YYYY-MM-DD") : null;
      const rowLocationId = row && row.filters ? toId(row.filters.location) : null;

      const matchesDate = currentFilters.date
        ? rowDate === moment(currentFilters.date).format("YYYY-MM-DD")
        : true;
      const matchesLocation = currentFilters.locationId
        ? rowLocationId === currentFilters.locationId.toString()
        : true;

      return matchesDate && matchesLocation;
    });
  };

  getPreferredVoucherId = (vouchers = []) => {
    const submittedVoucher = (vouchers || []).find(
      (row) => ((row && row.status) || "").toString().toUpperCase() === "SUBMITTED",
    );
    if (submittedVoucher && submittedVoucher._id) {
      return toId(submittedVoucher._id);
    }
    return vouchers && vouchers.length ? toId(vouchers[0]._id) : null;
  };

  ensureValidSelectionForFilters = async () => {
    const filteredVouchers = this.getFilteredVoucherOptions();
    const selectedVoucherId = this.state.selectedVoucherId ? this.state.selectedVoucherId.toString() : null;
    const hasSelected = selectedVoucherId
      && filteredVouchers.some((row) => row && toId(row._id) === selectedVoucherId);

    if (hasSelected) {
      return;
    }

    const nextVoucher = this.getPreferredVoucherId(filteredVouchers);
    await this.handleVoucherChange(nextVoucher);
  };

  handleDateFilterChange = async (date) => {
    await new Promise((resolve) => this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        date: date || null,
      },
    }), resolve));
    await this.ensureValidSelectionForFilters();
  };

  handleLocationFilterChange = async (locationId) => {
    await new Promise((resolve) => this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        locationId: locationId || undefined,
      },
    }), resolve));
    await this.ensureValidSelectionForFilters();
  };

  handleVoucherChange = async (voucherId) => {
    const nextVoucherId = voucherId ? voucherId.toString() : null;
    this.setState({
      selectedVoucherId: nextVoucherId,
      currentStockTaking: null,
      inventoryBySpare: {},
      selectedReconciliationRowKeys: [],
      pendingRefModal: {
        visible: false,
        rows: [],
      },
      bulkIgnoreModal: {
        visible: false,
        rows: [],
        remark: "",
      },
    });
    if (nextVoucherId) {
      await this.openStockTaking(nextVoucherId);
    }
  };

  getSelectedVoucherMeta = () => {
    const selectedVoucherId = this.state.selectedVoucherId ? this.state.selectedVoucherId.toString() : null;
    return (this.state.vouchers || []).find((row) => (
      row && toId(row._id) === selectedVoucherId
    )) || null;
  };

  getDeltaClass = (value) => {
    if (value === 0) {
      return "spares-delta-neutral";
    }
    if (value > 0) {
      return "spares-delta-positive";
    }
    return "spares-delta-negative";
  };

  getReconciliationData = () => {
    const currentStockTaking = this.state.currentStockTaking || {};
    const inventoryBySpare = this.state.inventoryBySpare || {};
    const records = Array.isArray(currentStockTaking.records) ? currentStockTaking.records : [];
    const canTakeActionInCurrentState = (currentStockTaking && currentStockTaking.status) === "SUBMITTED";

    const bySpare = {};
    records.forEach((row, index) => {
      const spareId = toId(row && row.spare);
      if (!spareId) {
        return;
      }

      if (!bySpare[spareId]) {
        bySpare[spareId] = {
          key: spareId,
          spareId,
          recordId: toId(row && row._id),
          pendingRecordIds: [],
          code: (row && row.spareCode) || "-",
          name: (row && row.spareName) || "-",
          category: (row && row.spareTypeName) || "-",
          uom: (row && row.unitName) || "-",
          stockTakingQty: 0,
          locationFootprint: `${(row && row.locationName) || "-"}${(row && row.subLocationName) ? ` / ${row.subLocationName}` : ""}`,
          action: "PENDING",
          ignoreRemark: "",
          savVoucherNo: "",
          savVoucherNos: [],
          savEntries: [],
          savCount: 0,
          adjustmentQuantity: 0,
          adjustmentRate: 0,
          adjustmentValue: 0,
          addedAmount: 0,
          reducedAmount: 0,
          processedAt: null,
          processedBy: null,
          _index: index,
        };
      }

      const current = bySpare[spareId];
      const rowRecordId = toId(row && row._id);
      if (!current.recordId && rowRecordId) {
        current.recordId = rowRecordId;
      }
      current.stockTakingQty += toNumber(row && row.countedQuantity);

      const rowAction = (row && row.action) || "PENDING";
      if (rowAction === "PENDING" && rowRecordId && current.pendingRecordIds.indexOf(rowRecordId) < 0) {
        current.pendingRecordIds.push(rowRecordId);
      }

      const rowSavEntries = Array.isArray(row && row.savEntries) ? row.savEntries : [];
      rowSavEntries.forEach((entry) => {
        const entryVoucherNo = entry && entry.voucherNo ? entry.voucherNo.toString() : "";
        if (!entryVoucherNo) {
          return;
        }
        if (current.savVoucherNos.indexOf(entryVoucherNo) < 0) {
          current.savVoucherNos.push(entryVoucherNo);
        }
        const entryId = toId(entry && entry._id);
        const entrySubLocationId = toId(entry && entry.subLocation) || "__none__";
        const entryQty = toNumber(entry && entry.adjustmentQuantity);
        const entryRate = toNumber(entry && entry.rate);
        const entryCreatedAt = entry && entry.createdAt ? entry.createdAt.toString() : "";
        const entryTransactionType = entry && entry.transactionType ? entry.transactionType.toString() : "";
        const dedupeKey = entryId
          || `${entryVoucherNo}|${entrySubLocationId}|${entryQty}|${entryRate}|${entryCreatedAt}|${entryTransactionType}`;
        const alreadySaved = (current.savEntries || []).some((saved) => {
          const savedId = toId(saved && saved._id);
          const savedVoucherNo = saved && saved.voucherNo ? saved.voucherNo.toString() : "";
          const savedSubLocationId = toId(saved && saved.subLocation) || "__none__";
          const savedQty = toNumber(saved && saved.adjustmentQuantity);
          const savedRate = toNumber(saved && saved.rate);
          const savedCreatedAt = saved && saved.createdAt ? saved.createdAt.toString() : "";
          const savedTransactionType = saved && saved.transactionType ? saved.transactionType.toString() : "";
          const savedKey = savedId
            || `${savedVoucherNo}|${savedSubLocationId}|${savedQty}|${savedRate}|${savedCreatedAt}|${savedTransactionType}`;
          return savedKey === dedupeKey;
        });
        if (!alreadySaved) {
          current.savEntries.push(entry);
        }
      });
      const rowSavVoucherNos = Array.isArray(row && row.savVoucherNos) ? row.savVoucherNos : [];
      rowSavVoucherNos.forEach((voucherNo) => {
        const normalizedVoucherNo = voucherNo ? voucherNo.toString() : "";
        if (normalizedVoucherNo && current.savVoucherNos.indexOf(normalizedVoucherNo) < 0) {
          current.savVoucherNos.push(normalizedVoucherNo);
        }
      });
      const singleSavVoucherNo = row && row.savVoucherNo ? row.savVoucherNo.toString() : "";
      if (singleSavVoucherNo && current.savVoucherNos.indexOf(singleSavVoucherNo) < 0) {
        current.savVoucherNos.push(singleSavVoucherNo);
      }

      if (rowAction === "ADJUSTMENT") {
        current.action = "ADJUSTMENT";
        current.savVoucherNo = current.savVoucherNo || singleSavVoucherNo || (current.savVoucherNos[0] || "");
      } else if (rowAction === "IGNORE" && current.action !== "ADJUSTMENT") {
        current.action = "IGNORE";
      }

      if (!current.ignoreRemark && rowAction === "IGNORE" && row && row.remarks) {
        current.ignoreRemark = row.remarks;
      }
      if (!current.processedAt && row && row.processedAt) {
        current.processedAt = row.processedAt;
      }
      if (!current.processedBy && row && row.processedBy) {
        current.processedBy = row.processedBy;
      }

      current.adjustmentQuantity += toNumber(row && row.adjustmentQuantity);
      let rowAddedAmount = 0;
      let rowReducedAmount = 0;
      if (rowSavEntries.length > 0) {
        rowSavEntries.forEach((entry) => {
          const entryQty = toNumber(entry && entry.adjustmentQuantity);
          const entryRate = toNumber(entry && entry.rate);
          const entryValue = toNumber(entry && entry.value) || Math.abs(entryQty * entryRate);
          const amount = Math.abs(entryValue);
          if (entryQty > 0) {
            rowAddedAmount += amount;
          } else if (entryQty < 0) {
            rowReducedAmount += amount;
          }
        });
      } else {
        const adjustmentQty = toNumber(row && row.adjustmentQuantity);
        const adjustmentRate = toNumber(row && row.adjustmentRate);
        const fallbackAmount = toNumber(row && row.adjustmentValue) || Math.abs(adjustmentQty * adjustmentRate);
        if (adjustmentQty > 0) {
          rowAddedAmount += fallbackAmount;
        } else if (adjustmentQty < 0) {
          rowReducedAmount += fallbackAmount;
        }
      }
      const rowAdjustmentValue = rowAddedAmount + rowReducedAmount;
      current.adjustmentValue = toNumber(current.adjustmentValue) + toNumber(rowAdjustmentValue);
      current.addedAmount = toNumber(current.addedAmount) + toNumber(rowAddedAmount);
      current.reducedAmount = toNumber(current.reducedAmount) + toNumber(rowReducedAmount);
      if (!current.adjustmentRate && toNumber(row && row.adjustmentRate)) {
        current.adjustmentRate = toNumber(row.adjustmentRate);
      }
      current.savCount = current.savVoucherNos.length;
    });

    Object.keys(bySpare).forEach((spareId) => {
      const row = bySpare[spareId];
      const qty = toNumber(row && row.adjustmentQuantity);
      const value = toNumber(row && row.adjustmentValue);
      if (qty !== 0 && value > 0) {
        row.adjustmentRate = Number((value / Math.abs(qty)).toFixed(6));
      }
    });

    const selectedSpareIds = new Set(Object.keys(bySpare));
    const selectedRows = Object.keys(bySpare)
      .map((spareId) => {
        const row = bySpare[spareId];
        const inventoryInfo = inventoryBySpare[spareId] || {};
        const inventoryQty = toNumber(inventoryInfo.quantity);
        const stockTakingQty = toNumber(row.stockTakingQty);
        const delta = Number((toNumber(row.stockTakingQty) - inventoryQty).toFixed(6));

        // Keep already processed rows: an adjustment can bring inventory down to zero.
        const isProcessed = row.action !== "PENDING" || row.savVoucherNos.length > 0;
        if (stockTakingQty === 0 && inventoryQty === 0 && !isProcessed) {
          return null;
        }

        let bucket = null;
        if (stockTakingQty > 0 && inventoryQty !== 0) {
          bucket = "spares-found";
        } else if (stockTakingQty <= 0) {
          bucket = "inventory-only";
        } else {
          bucket = "stock-taking-only";
        }

        const status = bucket === "spares-found"
          ? (delta === 0 ? "matched" : "variance")
          : (bucket === "stock-taking-only" ? "found-only" : "inventory-only");

        return {
          ...row,
          inventoryQty,
          locationFootprint: inventoryInfo.locationFootprint || "Not found in inventory",
          adjustmentValue: Number(toNumber(row.adjustmentValue).toFixed(2)),
          delta,
          status,
          bucket,
        };
      })
      .filter((row) => !!row);

    const inventoryOnlyRows = Object.keys(inventoryBySpare)
      .filter((spareId) => !selectedSpareIds.has(spareId))
      .map((spareId, index) => {
        const inventoryInfo = inventoryBySpare[spareId] || {};
        const inventoryQty = toNumber(inventoryInfo.quantity);
        if (inventoryQty === 0) {
          return null;
        }
        return {
          key: `inventory-only-${spareId}`,
          spareId,
          recordId: null,
          pendingRecordIds: [],
          code: inventoryInfo.code || "-",
          name: inventoryInfo.name || "-",
          category: inventoryInfo.category || "-",
          uom: inventoryInfo.uom || "-",
          stockTakingQty: 0,
          locationFootprint: inventoryInfo.locationFootprint || "Not found in inventory",
          action: canTakeActionInCurrentState ? "PENDING" : "LOCKED",
          ignoreRemark: "",
          savVoucherNo: "",
          savVoucherNos: [],
          savEntries: [],
          savCount: 0,
          adjustmentQuantity: 0,
          adjustmentRate: 0,
          adjustmentValue: 0,
          addedAmount: 0,
          reducedAmount: 0,
          processedAt: null,
          processedBy: null,
          inventoryQty,
          delta: Number((0 - inventoryQty).toFixed(6)),
          status: "inventory-only",
          bucket: "inventory-only",
          _index: (records.length || 0) + index,
        };
      })
      .filter((row) => !!row);

    const combinedRows = [...selectedRows, ...inventoryOnlyRows]
      .sort((a, b) => a._index - b._index);

    const sparesFound = combinedRows.filter((row) => row.bucket === "spares-found");
    const stockTakingOnly = combinedRows.filter((row) => row.bucket === "stock-taking-only");
    const inventoryOnly = combinedRows.filter((row) => row.bucket === "inventory-only");
    const ignoredRows = combinedRows.filter((row) => row.action === "IGNORE");
    const adjustedRows = combinedRows.filter((row) => row.action === "ADJUSTMENT");

    const getSummary = (rows) => ({
      totalItems: rows.length,
      adjusted: rows.filter((row) => row.action === "ADJUSTMENT").length,
      ignored: rows.filter((row) => row.action === "IGNORE").length,
      pending: rows.filter((row) => row.action === "PENDING").length,
    });

    const sectionSummaries = {
      sparesFound: getSummary(sparesFound),
      stockTakingOnly: getSummary(stockTakingOnly),
      inventoryOnly: getSummary(inventoryOnly),
      overall: getSummary(combinedRows),
    };

    const adjustmentSummary = adjustedRows.reduce(
      (acc, row) => {
        const addedAmount = toNumber(row.addedAmount);
        const reducedAmount = toNumber(row.reducedAmount);
        if (addedAmount > 0 || reducedAmount > 0) {
          acc.addedAmount += addedAmount;
          acc.reducedAmount += reducedAmount;
        } else {
          const adjustmentQty = toNumber(row.adjustmentQuantity);
          const rate = toNumber(row.adjustmentRate);
          const amount = toNumber(row.adjustmentValue) || (Math.abs(adjustmentQty) * rate);
          if (adjustmentQty > 0) {
            acc.addedAmount += amount;
          } else if (adjustmentQty < 0) {
            acc.reducedAmount += amount;
          }
        }
        (row.savVoucherNos || []).forEach((voucherNo) => {
          if (voucherNo) {
            acc.voucherSet.add(voucherNo);
          }
        });
        if (!(row.savVoucherNos || []).length && row.savVoucherNo) {
          acc.voucherSet.add(row.savVoucherNo);
        }
        return acc;
      },
      {
        addedAmount: 0,
        reducedAmount: 0,
        voucherSet: new Set(),
      },
    );

    const adjustmentVoucherMap = {};
    adjustedRows.forEach((row) => {
      const rowSavEntries = Array.isArray(row.savEntries) ? row.savEntries : [];
      if (rowSavEntries.length > 0) {
        rowSavEntries.forEach((entry) => {
          const voucherKey = (entry && entry.voucherNo) || row.key;
          if (!adjustmentVoucherMap[voucherKey]) {
            adjustmentVoucherMap[voucherKey] = {
              key: voucherKey,
              voucherNo: (entry && entry.voucherNo) || "-",
              createdAt: (entry && entry.createdAt) || row.processedAt || null,
              spareCodes: [],
              spareNames: [],
              addedAmount: 0,
              reducedAmount: 0,
            };
          }
          if (row.code && adjustmentVoucherMap[voucherKey].spareCodes.indexOf(row.code) < 0) {
            adjustmentVoucherMap[voucherKey].spareCodes.push(row.code);
          }
          if (row.name && adjustmentVoucherMap[voucherKey].spareNames.indexOf(row.name) < 0) {
            adjustmentVoucherMap[voucherKey].spareNames.push(row.name);
          }

          const entryQty = toNumber(entry && entry.adjustmentQuantity);
          const entryRate = toNumber(entry && entry.rate);
          const amount = toNumber(entry && entry.value) || Math.abs(entryQty * entryRate);
          if (entryQty > 0) {
            adjustmentVoucherMap[voucherKey].addedAmount += amount;
          } else if (entryQty < 0) {
            adjustmentVoucherMap[voucherKey].reducedAmount += amount;
          }
          if (!adjustmentVoucherMap[voucherKey].createdAt && row.processedAt) {
            adjustmentVoucherMap[voucherKey].createdAt = row.processedAt;
          }
        });
        return;
      }

      const voucherKey = (row.savVoucherNos && row.savVoucherNos[0]) || row.savVoucherNo || row.key;
      if (!adjustmentVoucherMap[voucherKey]) {
        adjustmentVoucherMap[voucherKey] = {
          key: voucherKey,
          voucherNo: (row.savVoucherNos && row.savVoucherNos[0]) || row.savVoucherNo || "-",
          createdAt: row.processedAt || null,
          spareCodes: [],
          spareNames: [],
          addedAmount: 0,
          reducedAmount: 0,
        };
      }
      if (row.code && adjustmentVoucherMap[voucherKey].spareCodes.indexOf(row.code) < 0) {
        adjustmentVoucherMap[voucherKey].spareCodes.push(row.code);
      }
      if (row.name && adjustmentVoucherMap[voucherKey].spareNames.indexOf(row.name) < 0) {
        adjustmentVoucherMap[voucherKey].spareNames.push(row.name);
      }

      const amount = toNumber(row.adjustmentValue) || Math.abs(toNumber(row.adjustmentQuantity) * toNumber(row.adjustmentRate));
      if (toNumber(row.adjustmentQuantity) > 0) {
        adjustmentVoucherMap[voucherKey].addedAmount += amount;
      } else if (toNumber(row.adjustmentQuantity) < 0) {
        adjustmentVoucherMap[voucherKey].reducedAmount += amount;
      }
      if (row.processedAt && !adjustmentVoucherMap[voucherKey].createdAt) {
        adjustmentVoucherMap[voucherKey].createdAt = row.processedAt;
      }
    });

    // Prefer the SST's full SAV list from the API so no voucher depends on row bucketing.
    if (Array.isArray(currentStockTaking.adjustmentVouchers)) {
      const sstVouchers = currentStockTaking.adjustmentVouchers.map((sav) => {
        const qty = toNumber(sav && sav.adjustmentQuantity);
        const amount = Math.abs(toNumber(sav && sav.value) || (qty * toNumber(sav && sav.rate)));
        const spareInfo = bySpare[toId(sav && sav.spare)] || {};
        const spareName = (sav && sav.spareName) || spareInfo.name || "";
        const spareCode = (sav && sav.spareCode) || spareInfo.code || "";
        return {
          key: toId(sav && sav._id) || (sav && sav.voucherNo),
          voucherNo: (sav && sav.voucherNo) || "-",
          createdAt: (sav && sav.createdAt) || null,
          spareCodes: spareCode ? [spareCode] : [],
          spareNames: spareName ? [spareName] : [],
          addedAmount: qty > 0 ? amount : 0,
          reducedAmount: qty < 0 ? amount : 0,
        };
      });

      return {
        sparesFound,
        stockTakingOnly,
        inventoryOnly,
        ignoredRows,
        adjustedRows,
        adjustmentVouchers: sstVouchers,
        sectionSummaries,
        adjustmentSummary: {
          voucherCount: sstVouchers.length,
          addedAmount: Number(sstVouchers.reduce((sum, row) => sum + row.addedAmount, 0).toFixed(2)),
          reducedAmount: Number(sstVouchers.reduce((sum, row) => sum + row.reducedAmount, 0).toFixed(2)),
        },
      };
    }

    return {
      sparesFound,
      stockTakingOnly,
      inventoryOnly,
      ignoredRows,
      adjustedRows,
      adjustmentVouchers: Object.values(adjustmentVoucherMap),
      sectionSummaries,
      adjustmentSummary: {
        voucherCount: adjustmentSummary.voucherSet.size || Object.keys(adjustmentVoucherMap).length,
        addedAmount: Number(adjustmentSummary.addedAmount.toFixed(2)),
        reducedAmount: Number(adjustmentSummary.reducedAmount.toFixed(2)),
      },
    };
  };

  getAllActionableRows = (reconciliationData) => {
    const sections = reconciliationData || this.getReconciliationData();
    return [
      ...(sections.sparesFound || []),
      ...(sections.stockTakingOnly || []),
      ...(sections.inventoryOnly || []),
    ];
  };

  isRowSelectableForBulkAction = (row) => {
    const { currentStockTaking } = this.state;
    if (!row || !currentStockTaking || currentStockTaking.status !== "SUBMITTED") {
      return false;
    }
    if (!this.canTakeAction()) {
      return false;
    }
    if (row.action !== "PENDING") {
      return false;
    }
    return !!this.getRowTargetRecordId(row);
  };

  getSelectedBulkActionRows = (reconciliationData) => {
    const { selectedReconciliationRowKeys } = this.state;
    const selectedKeySet = new Set((selectedReconciliationRowKeys || []).map((key) => key.toString()));
    return this.getAllActionableRows(reconciliationData)
      .filter((row) => selectedKeySet.has((row && row.key ? row.key.toString() : "")))
      .filter((row) => this.isRowSelectableForBulkAction(row));
  };

  getPendingReferenceRows = (reconciliationData) => {
    const sections = [
      { key: "sparesFound", label: "Spares Found" },
      { key: "stockTakingOnly", label: "Stock Taking Only" },
      { key: "inventoryOnly", label: "Inventory Only" },
    ];

    const rows = [];
    sections.forEach((section) => {
      const sectionRows = (reconciliationData && reconciliationData[section.key]) || [];
      sectionRows
        .filter((row) => row && row.action === "PENDING")
        .forEach((row) => {
          const status = ((row && row.status) || "").toString().toLowerCase();
          rows.push({
            key: `${section.key}-${row.key}`,
            section: section.label,
            code: row.code || "-",
            name: row.name || "-",
            category: row.category || "-",
            stockTakingQty: toNumber(row.stockTakingQty),
            inventoryQty: toNumber(row.inventoryQty),
            delta: toNumber(row.delta),
            status: row.status || "pending",
            reason: status === "matched"
              ? "Auto-matched row (no manual action needed)"
              : "Pending review action",
          });
        });
    });

    return rows;
  };

  openPendingReferenceModal = () => {
    const reconciliationData = this.getReconciliationData();
    const rows = this.getPendingReferenceRows(reconciliationData);
    if (!rows.length) {
      message.info("No pending rows found.");
      return;
    }

    this.setState({
      pendingRefModal: {
        visible: true,
        rows,
      },
    });
  };

  closePendingReferenceModal = () => {
    this.setState({
      pendingRefModal: {
        visible: false,
        rows: [],
      },
    });
  };

  handleReconciliationSelectionChange = (selectedRowKeys) => {
    this.setState({
      selectedReconciliationRowKeys: (selectedRowKeys || []).map((key) => key.toString()),
    });
  };

  closeBulkIgnoreModal = () => {
    this.setState({
      bulkIgnoreSaving: false,
      bulkIgnoreModal: {
        visible: false,
        rows: [],
        remark: "",
      },
    });
  };

  openBulkIgnoreModal = () => {
    const { currentStockTaking } = this.state;
    if (!currentStockTaking || currentStockTaking.status !== "SUBMITTED") {
      message.info("Only submitted SST supports bulk ignore");
      return;
    }

    const reconciliationData = this.getReconciliationData();
    const selectedRows = this.getSelectedBulkActionRows(reconciliationData);
    if (!selectedRows.length) {
      message.error("Please select at least one non-reviewed spare row");
      return;
    }

    this.setState({
      bulkIgnoreModal: {
        visible: true,
        rows: selectedRows,
        remark: "",
      },
    });
  };

  handleBulkIgnoreRemarkChange = (event) => {
    const nextValue = event && event.target ? event.target.value : "";
    this.setState((prevState) => ({
      bulkIgnoreModal: {
        ...prevState.bulkIgnoreModal,
        remark: nextValue,
      },
    }));
  };

  submitBulkIgnoreAction = async () => {
    const { currentStockTaking, bulkIgnoreModal } = this.state;
    if (!currentStockTaking || !currentStockTaking._id) {
      message.error("No selected SST for reconciliation");
      return;
    }

    const remarks = (bulkIgnoreModal && bulkIgnoreModal.remark ? bulkIgnoreModal.remark : "").trim();
    if (!remarks) {
      message.error("Ignore remark is required");
      return;
    }

    const selectedRows = Array.isArray(bulkIgnoreModal && bulkIgnoreModal.rows)
      ? bulkIgnoreModal.rows
      : [];
    if (!selectedRows.length) {
      message.error("No rows selected for bulk ignore");
      return;
    }

    const requests = [];
    selectedRows.forEach((row) => {
      const pendingRecordIds = Array.isArray(row && row.pendingRecordIds)
        ? row.pendingRecordIds.filter((value) => !!value)
        : [];
      if (pendingRecordIds.length > 0) {
        pendingRecordIds.forEach((recordId) => {
          requests.push({
            recordId,
            sparePart: row && row.spareId ? row.spareId : undefined,
          });
        });
      } else {
        const targetRecordId = this.getRowTargetRecordId(row);
        if (targetRecordId) {
          requests.push({
            recordId: targetRecordId,
            sparePart: row && row.spareId ? row.spareId : undefined,
          });
        }
      }
    });

    if (!requests.length) {
      message.error("No actionable records found in selected rows");
      return;
    }

    const requestMap = {};
    requests.forEach((request) => {
      const key = `${request.recordId || ""}|${request.sparePart || ""}`;
      if (!requestMap[key]) {
        requestMap[key] = request;
      }
    });
    const uniqueRequests = Object.values(requestMap);

    this.setState({ bulkIgnoreSaving: true });
    let latestUpdated = null;
    let processedCount = 0;
    try {
      for (let index = 0; index < uniqueRequests.length; index += 1) {
        const request = uniqueRequests[index];
        if (latestUpdated && (latestUpdated.status || "").toString().toUpperCase() === "CLOSED") {
          break;
        }
        const response = await axios.put(
          `/spare-stock-taking/${currentStockTaking._id}/records/${request.recordId}/action`,
          {
            action: "IGNORE",
            sparePart: request.sparePart,
            remarks,
          },
        );
        latestUpdated = (response && response.data) || latestUpdated;
        processedCount += 1;
      }

      if (!latestUpdated || !latestUpdated._id) {
        throw new Error("Invalid response while processing bulk ignore");
      }

      await this.applyUpdatedStockTakingState(latestUpdated, {
        bulkIgnoreSaving: false,
        selectedReconciliationRowKeys: [],
        bulkIgnoreModal: {
          visible: false,
          rows: [],
          remark: "",
        },
      });

      if ((latestUpdated.status || "").toString().toUpperCase() === "CLOSED") {
        this.showReconciliationSuccessPopup(
          latestUpdated,
          `Bulk ignore completed. ${latestUpdated.voucherNo} is reconciled.`,
        );
      } else {
        message.success(`${processedCount} record(s) marked as ignored`);
      }
    } catch (error) {
      this.setState({ bulkIgnoreSaving: false });
      this.showReconciliationErrorPopup(
        error,
        "Failed to process bulk ignore",
        "Bulk Ignore Failed",
      );
    }
  };

  buildAdjustmentAllocations = (row) => {
    if (!row) {
      return [];
    }
    const { currentStockTaking, inventoryBySpare } = this.state;
    const inventoryInfo = inventoryBySpare && inventoryBySpare[row.spareId]
      ? inventoryBySpare[row.spareId]
      : {};
    const inventoryDetails = Array.isArray(inventoryInfo.subLocationDetails)
      ? inventoryInfo.subLocationDetails
      : [];
    const inventoryQtyMap = {};
    inventoryDetails.forEach((entry) => {
      const key = entry && entry.id ? entry.id : "__none__";
      inventoryQtyMap[key] = toNumber(entry && entry.qty);
    });

    const contextSubLocationId = currentStockTaking && currentStockTaking.filters
      ? toId(currentStockTaking.filters.subLocation)
      : null;
    const hasContextSubLocation = !!contextSubLocationId;
    const inventoryKeys = Object.keys(inventoryQtyMap).map((key) => key || "__none__");
    const hasNoneInventoryKey = inventoryKeys.indexOf("__none__") >= 0;
    const hasRealSubLocationKeys = inventoryKeys.some((key) => key !== "__none__");
    const preferInventorySubLocationsOnly = !hasContextSubLocation
      && row
      && row.bucket === "inventory-only"
      && hasRealSubLocationKeys
      && !hasNoneInventoryKey;

    const sourceOrder = hasContextSubLocation
      ? [contextSubLocationId]
      : (preferInventorySubLocationsOnly ? [] : ["__none__"]);
    const pushKey = (key) => {
      if (sourceOrder.indexOf(key) < 0) {
        sourceOrder.push(key);
      }
    };
    if (!hasContextSubLocation) {
      Object.keys(inventoryQtyMap).forEach((key) => {
        const normalizedKey = key || "__none__";
        pushKey(normalizedKey);
      });
    }

    return sourceOrder.map((key, index) => {
      const isNone = key === "__none__";
      const subLocationId = isNone ? null : key;
      const subLocationName = isNone
        ? "No Sub-location"
        : this.getSubLocationNameById(key);
      const inventoryQty = toNumber(inventoryQtyMap[key]);

      let stockTakingQty = 0;
      if (contextSubLocationId) {
        stockTakingQty = contextSubLocationId === key ? toNumber(row.stockTakingQty) : 0;
      } else {
        stockTakingQty = isNone ? toNumber(row.stockTakingQty) : 0;
      }
      const defaultAdjustmentQty = toNumber(
        Number(stockTakingQty - inventoryQty).toFixed(6),
      );

      return {
        key: `${row.key}-${key}-${index}`,
        subLocationId,
        subLocationName,
        inventoryQty,
        stockTakingQty,
        adjustmentQty: defaultAdjustmentQty,
        manualRate: null,
        suggestedRate: 0,
        suggestedBasis: "NONE",
        suggestedSource: "",
        suggestedSourceNo: "",
        suggestedDate: null,
        suggestedResultingQty: toNumber(inventoryQty) + defaultAdjustmentQty,
        isManualRow: false,
      };
    });
  };

  getAdjustmentLocationSubLocations = () => {
    const { locations, currentStockTaking } = this.state;
    const locationId = currentStockTaking && currentStockTaking.filters
      ? toId(currentStockTaking.filters.location)
      : null;
    const location = (locations || []).find((entry) => toId(entry && entry._id) === locationId);
    return location && Array.isArray(location.subLocations)
      ? location.subLocations
      : [];
  };

  getAddableAdjustmentSubLocations = () => {
    const { adjustmentModal, subLocationNameMap, currentStockTaking } = this.state;
    const contextSubLocationId = currentStockTaking && currentStockTaking.filters
      ? toId(currentStockTaking.filters.subLocation)
      : null;
    if (contextSubLocationId) {
      return [];
    }
    const existingSubLocationIds = new Set(
      ((adjustmentModal && adjustmentModal.allocations) || [])
        .map((allocation) => (allocation && allocation.subLocationId ? allocation.subLocationId.toString() : null))
        .filter((value) => !!value),
    );

    const optionMap = {};
    this.getAdjustmentLocationSubLocations().forEach((subLocation) => {
      const subLocationId = toId(subLocation && (subLocation._id || subLocation.id));
      if (!subLocationId) {
        return;
      }
      optionMap[subLocationId] = {
        _id: subLocationId,
        name: (subLocation && subLocation.name) || "-",
      };
    });

    Object.keys(subLocationNameMap || {}).forEach((subLocationId) => {
      if (!subLocationId) {
        return;
      }
      if (!optionMap[subLocationId]) {
        optionMap[subLocationId] = {
          _id: subLocationId,
          name: (subLocationNameMap[subLocationId] || "-"),
        };
      }
    });

    return Object.values(optionMap)
      .filter((subLocation) => subLocation && subLocation._id && !existingSubLocationIds.has(subLocation._id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  };

  handleAdjustmentAddSubLocationChange = (value) => {
    this.setState((prevState) => ({
      adjustmentModal: {
        ...prevState.adjustmentModal,
        addSubLocationId: value || undefined,
      },
    }));
  };

  addAdjustmentSubLocationEntry = () => {
    const { adjustmentModal } = this.state;
    const selectedSubLocationId = adjustmentModal && adjustmentModal.addSubLocationId
      ? adjustmentModal.addSubLocationId.toString()
      : null;
    if (!selectedSubLocationId) {
      message.error("Please select a sub-location to add");
      return;
    }
    const alreadyExists = ((adjustmentModal && adjustmentModal.allocations) || []).some(
      (allocation) => allocation && allocation.subLocationId && allocation.subLocationId.toString() === selectedSubLocationId,
    );
    if (alreadyExists) {
      message.error("Selected sub-location is already added");
      return;
    }

    this.setState((prevState) => {
      const modal = prevState.adjustmentModal || {};
      const row = modal.row || null;
      const subLocationId = modal.addSubLocationId ? modal.addSubLocationId.toString() : selectedSubLocationId;
      if (!row || !subLocationId) {
        return null;
      }

      const inventoryInfo = prevState.inventoryBySpare && prevState.inventoryBySpare[row.spareId]
        ? prevState.inventoryBySpare[row.spareId]
        : {};
      const inventoryDetails = Array.isArray(inventoryInfo.allSubLocationDetails)
        ? inventoryInfo.allSubLocationDetails
        : [];
      const inventoryEntry = (inventoryDetails || []).find((entry) => entry && entry.id && entry.id.toString() === subLocationId);
      const inventoryQty = toNumber(inventoryEntry && inventoryEntry.qty);

      const contextSubLocationId = prevState.currentStockTaking && prevState.currentStockTaking.filters
        ? toId(prevState.currentStockTaking.filters.subLocation)
        : null;
      const stockTakingQty = contextSubLocationId && contextSubLocationId === subLocationId
        ? toNumber(row.stockTakingQty)
        : 0;

      const nextAllocations = [...(modal.allocations || []), {
        key: `${row.key}-${subLocationId}-manual-${Date.now()}`,
        subLocationId,
        subLocationName: this.getSubLocationNameById(subLocationId),
        inventoryQty,
        stockTakingQty,
        adjustmentQty: 0,
        manualRate: null,
        suggestedRate: 0,
        suggestedBasis: "NONE",
        suggestedSource: "",
        suggestedSourceNo: "",
        suggestedDate: null,
        suggestedResultingQty: inventoryQty,
        isManualRow: true,
      }];

      return {
        adjustmentModal: {
          ...modal,
          addSubLocationId: undefined,
          allocations: nextAllocations,
        },
      };
    }, () => {
      this.loadAdjustmentRateSuggestions();
    });
  };

  handlePendingRowAction = (action, row) => {
    if (action === "ADJUST") {
      this.openAdjustmentModal(row);
    }
  };

  openAdjustmentModal = (row) => {
    const targetRecordId = this.getRowTargetRecordId(row);
    if (!targetRecordId) {
      message.error("No actionable record found for selected row");
      return;
    }
    this.setState({
      adjustmentModal: {
        visible: true,
        row: {
          ...row,
          targetRecordId,
        },
        reason: "",
        addSubLocationId: undefined,
        allocations: this.buildAdjustmentAllocations(row),
      },
    }, () => {
      this.loadAdjustmentRateSuggestions();
    });
  };

  closeAdjustmentModal = () => {
    this.rateSuggestionRequestId += 1;
    this.setState({
      adjustmentSaving: false,
      adjustmentModal: {
        visible: false,
        row: null,
        reason: "",
        addSubLocationId: undefined,
        allocations: [],
      },
    });
  };

  loadAdjustmentRateSuggestions = async () => {
    const { currentStockTaking, adjustmentModal } = this.state;
    if (!currentStockTaking || !currentStockTaking._id || !adjustmentModal || !adjustmentModal.visible) {
      return;
    }
    const row = adjustmentModal.row || null;
    const recordId = row ? row.targetRecordId || this.getRowTargetRecordId(row) : null;
    if (!recordId) {
      return;
    }

    const sourceAllocations = Array.isArray(adjustmentModal.allocations)
      ? adjustmentModal.allocations
      : [];
    const payloadAllocations = sourceAllocations.map((allocation) => ({
      lineKey: allocation.key,
      subLocation: allocation && allocation.subLocationId ? allocation.subLocationId : null,
      adjustmentQuantity: toNumber(allocation && allocation.adjustmentQty),
      inventoryQuantity: toNumber(allocation && allocation.inventoryQty),
      adjustmentRate: toNumber(allocation && allocation.manualRate),
    }));

    const requestId = this.rateSuggestionRequestId + 1;
    this.rateSuggestionRequestId = requestId;

    try {
      const response = await axios.post(
        `/spare-stock-taking/${currentStockTaking._id}/records/${recordId}/adjustment-rate-suggestions`,
        {
          allocations: payloadAllocations,
          sparePart: row && row.spareId ? row.spareId : undefined,
        },
      );
      if (requestId !== this.rateSuggestionRequestId) {
        return;
      }

      const suggestions = (response && response.data && response.data.data) || [];
      const suggestionMap = {};
      (suggestions || []).forEach((item) => {
        if (item && item.lineKey) {
          suggestionMap[item.lineKey.toString()] = item;
        }
      });

      this.setState((prevState) => {
        const modal = prevState.adjustmentModal || {};
        const prevAllocations = Array.isArray(modal.allocations) ? modal.allocations : [];
        const nextAllocations = prevAllocations.map((allocation) => {
          const suggestion = suggestionMap[allocation.key] || null;
          return {
            ...allocation,
            suggestedRate: suggestion ? toNumber(suggestion.suggestedRate) : 0,
            suggestedBasis: suggestion ? (suggestion.basis || "NONE") : "NONE",
            suggestedSource: suggestion ? (suggestion.source || "") : "",
            suggestedSourceNo: suggestion ? (suggestion.sourceNo || "") : "",
            suggestedDate: suggestion ? (suggestion.referenceDate || null) : null,
            suggestedResultingQty: suggestion ? toNumber(suggestion.resultingQuantity) : toNumber(allocation.inventoryQty) + toNumber(allocation.adjustmentQty),
          };
        });

        return {
          adjustmentModal: {
            ...modal,
            allocations: nextAllocations,
          },
        };
      });
    } catch (error) {
      if (requestId !== this.rateSuggestionRequestId) {
        return;
      }
      this.setState((prevState) => {
        const modal = prevState.adjustmentModal || {};
        const prevAllocations = Array.isArray(modal.allocations) ? modal.allocations : [];
        const nextAllocations = prevAllocations.map((allocation) => ({
          ...allocation,
          suggestedRate: 0,
          suggestedBasis: "NONE",
          suggestedSource: "",
          suggestedSourceNo: "",
          suggestedDate: null,
          suggestedResultingQty: toNumber(allocation.inventoryQty) + toNumber(allocation.adjustmentQty),
        }));
        return {
          adjustmentModal: {
            ...modal,
            allocations: nextAllocations,
          },
        };
      });
    }
  };

  updateAdjustmentAllocation = (index, patch, options = {}) => {
    const shouldRefreshRates = options.shouldRefreshRates !== undefined
      ? options.shouldRefreshRates
      : Object.prototype.hasOwnProperty.call(patch || {}, "adjustmentQty");
    this.setState((prevState) => {
      const modal = prevState.adjustmentModal || {};
      const allocations = Array.isArray(modal.allocations) ? [...modal.allocations] : [];
      if (index < 0 || index >= allocations.length) {
        return null;
      }
      allocations[index] = {
        ...allocations[index],
        ...patch,
      };
      return {
        adjustmentModal: {
          ...modal,
          allocations,
        },
      };
    }, () => {
      if (shouldRefreshRates) {
        this.loadAdjustmentRateSuggestions();
      }
    });
  };

  handleAdjustmentReasonChange = (event) => {
    const nextValue = event && event.target ? event.target.value : "";
    this.setState((prevState) => ({
      adjustmentModal: {
        ...prevState.adjustmentModal,
        reason: nextValue,
      },
    }));
  };

  applyUpdatedStockTakingState = async (updated, extraState = {}) => {
    const updatedId = toId(updated && updated._id);
    let refreshedInventoryBySpare = this.state.inventoryBySpare || {};
    if (updated && updated._id) {
      refreshedInventoryBySpare = await this.fetchInventoryBySpare(updated);
    }
    await new Promise((resolve) => this.setState((prevState) => {
      let vouchers = prevState.vouchers || [];
      if (updated && updated.status === "SUBMITTED") {
        let updatedInList = false;
        vouchers = vouchers.map((voucher) => {
          if (toId(voucher && voucher._id) === updatedId) {
            updatedInList = true;
            return updated;
          }
          return voucher;
        });
        if (!updatedInList) {
          vouchers = [updated, ...vouchers];
        }
      } else {
        vouchers = vouchers.filter((voucher) => toId(voucher && voucher._id) !== updatedId);
      }

      const selectedVoucherId = toId(prevState.selectedVoucherId);
      const isCurrentSelected = selectedVoucherId && updatedId && selectedVoucherId === updatedId;

      return {
        currentStockTaking: updated && updated.status === "SUBMITTED"
          ? updated
          : (isCurrentSelected ? null : prevState.currentStockTaking),
        inventoryBySpare: updated && updated.status === "SUBMITTED"
          ? refreshedInventoryBySpare
          : (isCurrentSelected ? {} : prevState.inventoryBySpare),
        selectedVoucherId: updated && updated.status === "SUBMITTED"
          ? prevState.selectedVoucherId
          : (isCurrentSelected ? null : prevState.selectedVoucherId),
        vouchers,
        selectedReconciliationRowKeys: [],
        bulkIgnoreSaving: false,
        bulkIgnoreModal: {
          visible: false,
          rows: [],
          remark: "",
        },
        ...extraState,
      };
    }, resolve));
  };

  submitAdjustmentAction = async () => {
    const { currentStockTaking, adjustmentModal } = this.state;
    if (!currentStockTaking || !currentStockTaking._id) {
      message.error("No selected SST for reconciliation");
      return;
    }
    const row = (adjustmentModal && adjustmentModal.row) || null;
    const recordId = row ? row.targetRecordId || this.getRowTargetRecordId(row) : null;
    if (!recordId) {
      message.error("No actionable record found for adjustment");
      return;
    }

    const allocations = Array.isArray(adjustmentModal.allocations)
      ? adjustmentModal.allocations
      : [];
    const effectiveAllocations = allocations
      .map((allocation) => {
        const manualRate = toNumber(allocation && allocation.manualRate);
        const suggestedRate = toNumber(allocation && allocation.suggestedRate);
        return {
          lineKey: allocation && allocation.key ? allocation.key : undefined,
          subLocation: allocation && allocation.subLocationId ? allocation.subLocationId : null,
          adjustmentQuantity: toNumber(allocation && allocation.adjustmentQty),
          adjustmentRate: manualRate > 0 ? manualRate : suggestedRate,
          inventoryQuantity: toNumber(allocation && allocation.inventoryQty),
          countedQuantity: toNumber(allocation && allocation.stockTakingQty),
        };
      })
      .filter((allocation) => allocation.adjustmentQuantity !== 0);

    if (!effectiveAllocations.length) {
      message.error("Please enter adjustment quantity in at least one distribution row");
      return;
    }

    const invalidRateRow = effectiveAllocations.find((allocation) => !(toNumber(allocation.adjustmentRate) > 0));
    if (invalidRateRow) {
      message.error("Rate is required for all non-zero adjustment rows");
      return;
    }

    this.setState({ adjustmentSaving: true });
    try {
      const response = await axios.put(
        `/spare-stock-taking/${currentStockTaking._id}/records/${recordId}/action`,
        {
          action: "ADJUSTMENT",
          sparePart: row && row.spareId ? row.spareId : undefined,
          remarks: (adjustmentModal && adjustmentModal.reason ? adjustmentModal.reason : "").trim(),
          allocations: effectiveAllocations,
        },
      );

      const updated = (response && response.data) || null;
      if (!updated || !updated._id) {
        throw new Error("Invalid response while applying adjustment action");
      }

      await this.applyUpdatedStockTakingState(updated, {
        adjustmentSaving: false,
        adjustmentModal: {
          visible: false,
          row: null,
          reason: "",
          addSubLocationId: undefined,
          allocations: [],
        },
      });

      if (updated.status === "CLOSED") {
        this.showReconciliationSuccessPopup(
          updated,
          `Adjustment saved. ${updated.voucherNo} is reconciled.`,
        );
      } else {
        message.success("Adjustment voucher prepared successfully");
      }
    } catch (error) {
      this.setState({ adjustmentSaving: false });
      this.showReconciliationErrorPopup(
        error,
        "Failed to prepare adjustment voucher",
        "Reconciliation Failed",
      );
    }
  };

  getRowTargetRecordId = (row) => {
    if (!row) {
      return null;
    }
    const pendingRecordIds = Array.isArray(row.pendingRecordIds) ? row.pendingRecordIds : [];
    if (pendingRecordIds.length > 0) {
      return pendingRecordIds[0];
    }
    if (row.spareId) {
      return `inventory-only-${row.spareId}`;
    }
    return row.recordId || null;
  };

  openIgnoreModal = (row) => {
    const targetRecordId = this.getRowTargetRecordId(row);
    if (!targetRecordId) {
      message.error("No actionable record found for selected row");
      return;
    }
    this.setState({
      ignoreModal: {
        visible: true,
        mode: "create",
        row: {
          ...row,
          targetRecordId,
        },
        remark: "",
      },
    });
  };

  openIgnoreRemarkModal = (row) => {
    this.setState({
      ignoreModal: {
        visible: true,
        mode: "view",
        row: row || null,
        remark: (row && row.ignoreRemark) || "",
      },
    });
  };

  openSavModal = (row) => {
    this.setState({
      savModal: {
        visible: true,
        row: row || null,
      },
    });
  };

  closeSavModal = () => {
    this.setState({
      savModal: {
        visible: false,
        row: null,
      },
    });
  };

  getSavDetailRows = (row) => {
    if (!row) {
      return [];
    }
    const rowSavEntries = Array.isArray(row.savEntries) ? row.savEntries : [];
    if (rowSavEntries.length > 0) {
      return rowSavEntries.map((entry, index) => {
        const hasOriginalQuantity = entry && entry.originalQuantity !== undefined && entry.originalQuantity !== null;
        const originalQuantity = hasOriginalQuantity ? toNumber(entry.originalQuantity) : null;
        const adjustmentQuantity = toNumber(entry && entry.adjustmentQuantity);
        const resultingQuantity = hasOriginalQuantity
          ? toNumber(Number(originalQuantity + adjustmentQuantity).toFixed(6))
          : null;
        const rate = toNumber(entry && entry.rate);
        const value = toNumber(entry && entry.value) || Math.abs(adjustmentQuantity * rate);
        const subLocationId = entry && entry.subLocation ? entry.subLocation : null;
        return {
          key: (entry && entry._id) || `${row.key}-sav-${index}`,
          voucherNo: entry && entry.voucherNo ? entry.voucherNo : "-",
          transactionType: entry && entry.transactionType ? entry.transactionType : "",
          subLocationName: subLocationId ? this.getSubLocationNameById(subLocationId) : "No Sub-location",
          originalQuantity,
          adjustmentQuantity,
          resultingQuantity,
          rate,
          value,
          remarks: entry && entry.remarks ? entry.remarks : "",
          createdAt: entry && entry.createdAt ? entry.createdAt : null,
        };
      });
    }

    const voucherNos = (Array.isArray(row.savVoucherNos) && row.savVoucherNos.length > 0)
      ? row.savVoucherNos
      : ((row && row.savVoucherNo) ? [row.savVoucherNo] : []);
    if (!voucherNos.length) {
      return [];
    }

    const qty = toNumber(row.adjustmentQuantity);
    const rate = toNumber(row.adjustmentRate);
    const value = Math.abs(qty * rate);
    return voucherNos.map((voucherNo, index) => ({
      key: `${row.key}-${voucherNo}-${index}`,
      voucherNo: voucherNo || "-",
      transactionType: qty >= 0 ? "ADJUSTMENT_INWARD" : "ADJUSTMENT_OUTWARD",
      subLocationName: row && row.locationFootprint ? row.locationFootprint : "-",
      originalQuantity: null,
      adjustmentQuantity: index === 0 ? qty : 0,
      resultingQuantity: null,
      rate: index === 0 ? rate : 0,
      value: index === 0 ? value : 0,
      remarks: row && row.remarks ? row.remarks : "",
      createdAt: row && row.processedAt ? row.processedAt : null,
    }));
  };

  closeIgnoreModal = () => {
    this.setState({
      ignoreModal: {
        visible: false,
        mode: "create",
        row: null,
        remark: "",
      },
      ignoreSaving: false,
    });
  };

  handleIgnoreRemarkChange = (event) => {
    const nextValue = event && event.target ? event.target.value : "";
    this.setState((prevState) => ({
      ignoreModal: {
        ...prevState.ignoreModal,
        remark: nextValue,
      },
    }));
  };

  submitIgnoreAction = async () => {
    const { currentStockTaking, ignoreModal } = this.state;
    if (!currentStockTaking || !currentStockTaking._id) {
      message.error("No selected SST for reconciliation");
      return;
    }
    const row = (ignoreModal && ignoreModal.row) || null;
    const recordId = row ? row.targetRecordId || this.getRowTargetRecordId(row) : null;
    if (!recordId) {
      message.error("No actionable record found for ignore");
      return;
    }

    const remarks = (ignoreModal && ignoreModal.remark ? ignoreModal.remark : "").trim();
    if (!remarks) {
      message.error("Ignore remark is required");
      return;
    }

    this.setState({ ignoreSaving: true });
    try {
      const response = await axios.put(
        `/spare-stock-taking/${currentStockTaking._id}/records/${recordId}/action`,
        {
          action: "IGNORE",
          sparePart: row && row.spareId ? row.spareId : undefined,
          remarks,
        },
      );
      const updated = (response && response.data) || null;
      if (!updated || !updated._id) {
        throw new Error("Invalid response while applying ignore action");
      }

      await this.applyUpdatedStockTakingState(updated, {
        ignoreSaving: false,
        ignoreModal: {
          visible: false,
          mode: "create",
          row: null,
          remark: "",
        },
      });

      if (updated.status === "CLOSED") {
        this.showReconciliationSuccessPopup(
          updated,
          `Record reviewed. ${updated.voucherNo} is reconciled.`,
        );
      } else {
        message.success("Record marked as ignored");
      }
    } catch (error) {
      this.setState({ ignoreSaving: false });
      this.showReconciliationErrorPopup(
        error,
        "Failed to mark record as ignored",
        "Reconciliation Failed",
      );
    }
  };

  recheckAndCloseSst = async () => {
    const { currentStockTaking } = this.state;
    if (!currentStockTaking || !currentStockTaking._id) {
      message.error("No selected SST for reconciliation");
      return;
    }
    if ((currentStockTaking.status || "").toString().toUpperCase() !== "SUBMITTED") {
      message.info("Only submitted SST can be rechecked for closure");
      return;
    }

    this.setState({ recheckClosing: true });
    try {
      const response = await axios.put(`/spare-stock-taking/${currentStockTaking._id}/recheck-close`);
      const updated = (response && response.data) || null;
      if (!updated || !updated._id) {
        throw new Error("Invalid response while rechecking SST closure");
      }

      await this.applyUpdatedStockTakingState(updated, { recheckClosing: false });

      if ((updated.status || "").toString().toUpperCase() === "CLOSED") {
        this.showReconciliationSuccessPopup(
          updated,
          `Recheck completed. ${updated.voucherNo} is reconciled.`,
        );
      } else {
        message.info("Recheck completed. SST still has pending reconciliation actions.");
      }
    } catch (error) {
      this.setState({ recheckClosing: false });
      this.showReconciliationErrorPopup(
        error,
        "Failed to recheck SST closure",
        "Recheck Failed",
      );
    }
  };

  formatCurrency = (value) => `Rs ${Number(value || 0).toFixed(2)}`;

  render() {
    const {
      loadingList,
      loadingDetails,
      filters,
      ignoreModal,
      ignoreSaving,
      adjustmentModal,
      adjustmentSaving,
      savModal,
      pendingRefModal,
      bulkIgnoreModal,
      bulkIgnoreSaving,
      locations,
      recheckClosing,
      selectedReconciliationRowKeys,
      selectedVoucherId,
      currentStockTaking,
    } = this.state;

    const filteredVoucherOptions = this.getFilteredVoucherOptions();
    const selectedVoucherMeta = this.getSelectedVoucherMeta();
    const selectedLocationName = selectedVoucherMeta && selectedVoucherMeta.filters
      ? toName(selectedVoucherMeta.filters.location)
      : "-";
    const selectedSubLocationName = selectedVoucherMeta && selectedVoucherMeta.filters
      ? toName(selectedVoucherMeta.filters.subLocation)
      : "-";
    const canTakeAction = this.canTakeAction();
    const isVoucherLoading = !!selectedVoucherId && loadingDetails;
    const reconciliationData = this.getReconciliationData();
    const selectableRowKeySet = new Set(
      this.getAllActionableRows(reconciliationData)
        .filter((row) => this.isRowSelectableForBulkAction(row))
        .map((row) => (row && row.key ? row.key.toString() : ""))
        .filter((key) => !!key),
    );
    const effectiveSelectedRowKeys = (selectedReconciliationRowKeys || [])
      .map((key) => key.toString())
      .filter((key) => selectableRowKeySet.has(key));
    const selectedBulkRows = this.getSelectedBulkActionRows(reconciliationData);
    const selectedBulkRowsCount = selectedBulkRows.length;
    const pendingReferenceRows = (pendingRefModal && Array.isArray(pendingRefModal.rows))
      ? pendingRefModal.rows
      : [];
    const rowSelectionConfig = {
      selectedRowKeys: effectiveSelectedRowKeys,
      onChange: this.handleReconciliationSelectionChange,
      getCheckboxProps: (record) => ({
        disabled: !this.isRowSelectableForBulkAction(record),
      }),
    };

    const baseColumns = [
      {
        title: this.tooltipLabel("Spare", "Spare item from SST or inventory"),
        dataIndex: "name",
        key: "name",
        render: (value, record) => (
          <div>
            <div className="spares-cell-strong">{value}</div>
            <div className="spares-cell-subtle">{record.code} • {record.category}</div>
          </div>
        ),
      },
      {
        title: this.tooltipLabel("UOM", "Unit of measurement"),
        dataIndex: "uom",
        key: "uom",
        width: 90,
      },
      {
        title: this.tooltipLabel("Stock Taking Qty", "Quantity entered in SST"),
        dataIndex: "stockTakingQty",
        key: "stockTakingQty",
        align: "right",
        width: 140,
        render: (value) => Number(value || 0).toLocaleString(),
      },
      {
        title: this.tooltipLabel("Inventory Qty", "System quantity available for this row"),
        dataIndex: "inventoryQty",
        key: "inventoryQty",
        align: "right",
        width: 120,
        render: (value) => Number(value || 0).toLocaleString(),
      },
      {
        title: this.tooltipLabel("Difference", "Stock taking minus inventory"),
        dataIndex: "delta",
        key: "delta",
        align: "right",
        width: 120,
        render: (value) => (
          <span className={this.getDeltaClass(value)}>
            {value > 0 ? "+" : ""}{value}
          </span>
        ),
      },
      {
        title: this.tooltipLabel("Adjustment Qty", "Final adjusted quantity for reviewed adjusted rows"),
        dataIndex: "adjustmentQuantity",
        key: "adjustmentQuantity",
        align: "right",
        width: 130,
        render: (value, record) => {
          if (record.action !== "ADJUSTMENT") {
            return "-";
          }
          const qty = toNumber(value);
          return `${qty > 0 ? "+" : ""}${qty.toLocaleString()}`;
        },
      },
      {
        title: this.tooltipLabel("Applied Rate", "Final applied rate for reviewed adjusted rows"),
        dataIndex: "adjustmentRate",
        key: "adjustmentRate",
        align: "right",
        width: 120,
        render: (value, record) => (
          record.action === "ADJUSTMENT" && toNumber(value) > 0
            ? `Rs ${toNumber(value).toFixed(2)}`
            : "-"
        ),
      },
      {
        title: this.tooltipLabel("Adjustment Value", "Total value of reviewed adjustment"),
        dataIndex: "adjustmentValue",
        key: "adjustmentValue",
        align: "right",
        width: 140,
        render: (value, record) => {
          if (record.action !== "ADJUSTMENT") {
            return "-";
          }
          const fallbackValue = Math.abs(toNumber(record.adjustmentQuantity) * toNumber(record.adjustmentRate));
          const displayValue = toNumber(value) || fallbackValue;
          return this.formatCurrency(displayValue);
        },
      },
      {
        title: this.tooltipLabel("Inventory Footprint", "Location and sub location context"),
        dataIndex: "locationFootprint",
        key: "locationFootprint",
        render: (value) => <span className="spares-cell-subtle">{value || "-"}</span>,
      },
      {
        title: this.tooltipLabel("Status", "Current reconciliation action state"),
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (status, record) => {
          if (record.action === "ADJUSTMENT") {
            return <Tag color="blue">Adjusted</Tag>;
          }
          if (record.action === "IGNORE") {
            return (
              <button
                type="button"
                className="spares-link-button spares-status-link-ignore"
                onClick={() => this.openIgnoreRemarkModal(record)}
              >
                Ignored
              </button>
            );
          }
          if (status === "matched") {
            return <Tag color="green">Matched</Tag>;
          }
          if (status === "variance") {
            return <Tag color="orange">Variance</Tag>;
          }
          if (status === "found-only") {
            return <Tag color="gold">Found Only</Tag>;
          }
          if (status === "inventory-only") {
            return <Tag color="red">Inventory Only</Tag>;
          }
          return <Tag>Pending</Tag>;
        },
      },
    ];

    const actionColumn = {
      title: this.tooltipLabel("Actions", "Ignore or adjust variance"),
      key: "actionCell",
      width: 170,
      render: (_, record) => {
        if (record.action === "ADJUSTMENT") {
          const savCount = toNumber(record.savCount) || ((record.savVoucherNos || []).length || (record.savVoucherNo ? 1 : 0));
          if (!savCount) {
            return <Tag color="blue">Adjusted</Tag>;
          }
          return (
            <button
              type="button"
              className="spares-link-button spares-status-link-sav"
              onClick={() => this.openSavModal(record)}
            >
              {`${savCount} SAV`}
            </button>
          );
        }
        if (record.action === "LOCKED") {
          return <Tag color="blue">Locked</Tag>;
        }
        if (record.action !== "PENDING") {
          return <Tag color="green">Reviewed</Tag>;
        }
        if ((currentStockTaking && currentStockTaking.status) !== "SUBMITTED") {
          return <Tag color="blue">Locked</Tag>;
        }
        if (record.status === "matched") {
          return <Tag color="green">Reviewed</Tag>;
        }
        const targetRecordId = this.getRowTargetRecordId(record);
        if (!targetRecordId) {
          return <Tag color="blue">Locked</Tag>;
        }
        return (
          <div className="spares-row-actions">
            <Button
              size="small"
              className="spares-btn spares-btn-open"
              disabled={!canTakeAction}
              onClick={() => this.openIgnoreModal(record)}
            >
              Ignore
            </Button>
            <Button
              size="small"
              className="spares-btn spares-btn-reconcile"
              disabled={!canTakeAction}
              onClick={() => this.handlePendingRowAction("ADJUST", record)}
            >
              Adjust
            </Button>
          </div>
        );
      },
    };

    const adjustmentModalRow = adjustmentModal && adjustmentModal.row ? adjustmentModal.row : null;
    const adjustmentModalAllocations = adjustmentModal && Array.isArray(adjustmentModal.allocations)
      ? adjustmentModal.allocations
      : [];
    const savModalRow = savModal && savModal.row ? savModal.row : null;
    const savModalRows = this.getSavDetailRows(savModalRow);
    const savModalColumns = [
      {
        title: this.tooltipLabel("SAV Voucher", "Spares Adjustment Voucher number"),
        dataIndex: "voucherNo",
        key: "voucherNo",
        width: 140,
      },
      {
        title: this.tooltipLabel("Type", "Adjustment direction"),
        dataIndex: "transactionType",
        key: "transactionType",
        width: 140,
        render: (value) => {
          const normalizedType = value ? value.toString().toUpperCase() : "";
          if (normalizedType.indexOf("OUTWARD") >= 0) {
            return <Tag color="orange">OUTWARD</Tag>;
          }
          if (normalizedType.indexOf("INWARD") >= 0) {
            return <Tag color="green">INWARD</Tag>;
          }
          return <Tag>-</Tag>;
        },
      },
      {
        title: this.tooltipLabel("Sub-location", "Sub-location context for SAV"),
        dataIndex: "subLocationName",
        key: "subLocationName",
        width: 180,
        render: (value) => value || "-",
      },
      {
        title: this.tooltipLabel("Original Qty", "Quantity before this adjustment"),
        dataIndex: "originalQuantity",
        key: "originalQuantity",
        width: 120,
        align: "right",
        render: (value) => {
          if (value === null || value === undefined) {
            return "-";
          }
          return toNumber(value).toLocaleString();
        },
      },
      {
        title: this.tooltipLabel("Adjustment Qty", "Adjusted quantity in this SAV row"),
        dataIndex: "adjustmentQuantity",
        key: "adjustmentQuantity",
        width: 120,
        align: "right",
        render: (value) => {
          const qty = toNumber(value);
          return `${qty > 0 ? "+" : ""}${qty.toLocaleString()}`;
        },
      },
      {
        title: this.tooltipLabel("Resulting Qty", "Quantity after this adjustment"),
        dataIndex: "resultingQuantity",
        key: "resultingQuantity",
        width: 120,
        align: "right",
        render: (value) => {
          if (value === null || value === undefined) {
            return "-";
          }
          return toNumber(value).toLocaleString();
        },
      },
      {
        title: this.tooltipLabel("Rate", "Applied SAV rate"),
        dataIndex: "rate",
        key: "rate",
        width: 110,
        align: "right",
        render: (value) => (toNumber(value) > 0 ? `Rs ${toNumber(value).toFixed(2)}` : "-"),
      },
      {
        title: this.tooltipLabel("Value", "Absolute value for SAV row"),
        dataIndex: "value",
        key: "value",
        width: 130,
        align: "right",
        render: (value) => this.formatCurrency(toNumber(value)),
      },
      {
        title: this.tooltipLabel("Remarks", "Adjustment remark"),
        dataIndex: "remarks",
        key: "remarks",
        render: (value) => value || "-",
      },
      {
        title: this.tooltipLabel("Created At", "SAV creation time"),
        dataIndex: "createdAt",
        key: "createdAt",
        width: 160,
        render: (value) => value ? moment(value).format("DD MMM YYYY HH:mm") : "-",
      },
    ];
    const addableSubLocations = this.getAddableAdjustmentSubLocations();
    const selectedAddSubLocationId = adjustmentModal && adjustmentModal.addSubLocationId
      ? adjustmentModal.addSubLocationId
      : undefined;
    const rateSummaryText = adjustmentModalAllocations
      .filter((allocation) => toNumber(allocation && allocation.adjustmentQty) !== 0)
      .map((allocation) => {
        const qty = toNumber(allocation && allocation.adjustmentQty);
        const basis = (allocation && allocation.suggestedBasis && allocation.suggestedBasis !== "NONE")
          ? allocation.suggestedBasis
          : (qty > 0 ? "LIFO" : "FIFO");
        return `${allocation.subLocationName}: ${basis}`;
      })
      .join(" • ");
    const getSuggestionText = (allocation) => {
      const qty = toNumber(allocation && allocation.adjustmentQty);
      if (!qty) {
        return "Enter adjustment quantity to view suggested rate.";
      }
      const suggestedRate = toNumber(allocation && allocation.suggestedRate);
      if (suggestedRate > 0) {
        const source = (allocation && allocation.suggestedSource) || "Ledger history";
        const sourceNo = (allocation && allocation.suggestedSourceNo) || "";
        const dateText = allocation && allocation.suggestedDate
          ? moment(allocation.suggestedDate).format("DD MMM YYYY")
          : "";
        return (
          <span>
            Suggested: Rs {suggestedRate.toFixed(2)}
            {sourceNo ? <span> • <strong>{sourceNo}</strong></span> : null}
            {source ? <span> • {source}</span> : null}
            {dateText ? <span> • {dateText}</span> : null}
          </span>
        );
      }
      return qty > 0
        ? "No LIFO suggestion found. Please enter rate."
        : "No FIFO suggestion found from ledger.";
    };
    const adjustmentPlanColumns = [
      {
        title: this.tooltipLabel("Sub-location", "Distribution line for voucher preparation"),
        dataIndex: "subLocationName",
        key: "subLocationName",
        width: 220,
      },
      {
        title: this.tooltipLabel("Inventory Qty", "Current inventory quantity in this sub-location"),
        dataIndex: "inventoryQty",
        key: "inventoryQty",
        width: 95,
        align: "right",
        render: (value) => Number(value || 0).toLocaleString(),
      },
      {
        title: this.tooltipLabel("Stock Taking Qty", "Counted quantity against this context"),
        dataIndex: "stockTakingQty",
        key: "stockTakingQty",
        width: 95,
        align: "right",
        render: (value) => Number(value || 0).toLocaleString(),
      },
      {
        title: this.tooltipLabel("Adjustment Qty", "Enter + or - quantity to adjust"),
        dataIndex: "adjustmentQty",
        key: "adjustmentQty",
        width: 95,
        align: "right",
        render: (value, record, index) => (
          <InputNumber
            className="spares-qty-input"
            value={value}
            style={{ width: 90 }}
            onChange={(nextValue) => this.updateAdjustmentAllocation(index, { adjustmentQty: toNumber(nextValue) }, { shouldRefreshRates: true })}
          />
        ),
      },
      {
        title: this.tooltipLabel("Resulting Qty", "Inventory quantity after the entered adjustment"),
        key: "resultingQty",
        width: 95,
        align: "right",
        render: (_, record) => Number(toNumber(record && record.suggestedResultingQty)).toLocaleString(),
      },
      {
        title: this.tooltipLabel("Basis", "LIFO for +ve and FIFO for -ve adjustments"),
        key: "rateBasis",
        width: 85,
        render: (_, record) => {
          const qty = toNumber(record && record.adjustmentQty);
          if (!qty) {
            return <span className="spares-cell-subtle">-</span>;
          }
          const basis = (record && record.suggestedBasis && record.suggestedBasis !== "NONE")
            ? record.suggestedBasis
            : (qty > 0 ? "LIFO" : "FIFO");
          return basis === "LIFO" ? <Tag color="green">LIFO</Tag> : <Tag color="orange">FIFO</Tag>;
        },
      },
      {
        title: this.tooltipLabel("Applied Rate", "Manual or suggested rate used for this row."),
        dataIndex: "manualRate",
        key: "manualRate",
        width: 145,
        render: (value, record, index) => {
          const suggestedRate = toNumber(record && record.suggestedRate);
          const hasManualRate = value !== null && value !== undefined && value !== "";
          const rateInputValue = hasManualRate
            ? value
            : (suggestedRate > 0 ? suggestedRate : undefined);
          const suggestionText = getSuggestionText(record);
          return (
            <div className="spares-rate-cell-inline">
              <InputNumber
                className="spares-rate-input"
                min={0}
                value={rateInputValue}
                style={{ width: 78 }}
                placeholder={toNumber(record && record.adjustmentQty) !== 0 ? "Required" : "-"}
                onChange={(nextValue) => this.updateAdjustmentAllocation(index, { manualRate: nextValue }, { shouldRefreshRates: false })}
              />
              <Tooltip title={suggestionText}>
                <button type="button" className="spares-rate-info-btn" tabIndex={-1}>
                  <Icon type="info-circle" />
                </button>
              </Tooltip>
            </div>
          );
        },
      },
      {
        title: this.tooltipLabel("Adjustment Value", "Absolute value for this line based on qty and adjustment rate."),
        key: "adjustmentValue",
        width: 120,
        align: "right",
        render: (_, record) => {
          const qty = toNumber(record && record.adjustmentQty);
          const manualRate = toNumber(record && record.manualRate);
          const suggestedRate = toNumber(record && record.suggestedRate);
          const effectiveRate = manualRate > 0 ? manualRate : suggestedRate;
          if (!qty || !(effectiveRate > 0)) {
            return "-";
          }
          return this.formatCurrency(Math.abs(qty) * effectiveRate);
        },
      },
    ];

    return (
      <div className="spares-page">
        <div className="spares-hero">
          <div>
            <h3>Reconcile counted spares against inventory for the selected submitted SST.</h3>
            <p>
              Use Ignore to mark variances as reviewed with a compulsory remark. Reviewed rows are locked from further action.
            </p>
          </div>
        </div>

        <Card
          className="spares-panel-card spares-filter-card"
          title={this.tooltipLabel("Reconciliation Filters", "Select date, location and SST voucher")}
          extra={(
            <Button className="spares-btn" onClick={this.openAdjustmentVoucherReport}>
              Spare Adjustment Vouchers
            </Button>
          )}
        >
          <div className="spares-form-grid">
            <div className="spares-form-field">
              <span>{this.tooltipLabel("Date", "Filter SST by stock taking date")}</span>
              <DatePicker
                value={filters.date}
                style={{ width: "100%" }}
                onChange={this.handleDateFilterChange}
              />
            </div>
            <div className="spares-form-field">
              <span>{this.tooltipLabel("Location", "Filter by location")}</span>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={selectFilterByChildren}
                value={filters.locationId || undefined}
                placeholder="Filter by location"
                onChange={this.handleLocationFilterChange}
              >
                {(locations || []).map((location) => (
                  <Select.Option key={location._id} value={location._id}>
                    {location.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="spares-form-field">
              <span>{this.tooltipLabel("Source Voucher", "Submitted or closed SST voucher")}</span>
              <Select
                value={selectedVoucherId || undefined}
                placeholder="Select voucher"
                loading={loadingList}
                allowClear
                onChange={this.handleVoucherChange}
              >
                {(filteredVoucherOptions || []).map((voucher) => (
                  <Select.Option key={voucher._id} value={voucher._id}>
                    {voucher.voucherNo} • {voucher.filters ? toName(voucher.filters.location) : "-"}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
        </Card>

        {isVoucherLoading ? (
          <Card className="spares-panel-card">
            <div style={{ padding: "56px 0", textAlign: "center" }}>
              <Spin size="large" tip="Loading reconciliation data..." />
            </div>
          </Card>
        ) : (!selectedVoucherId || !currentStockTaking) ? (
          <Card className="spares-panel-card">
            <Empty description="Select an SST voucher to view reconciliation sections." />
          </Card>
        ) : (
          <div>
            <Alert
              type="info"
              showIcon
              className="spares-alert"
              message={`Reconciling ${selectedVoucherMeta ? selectedVoucherMeta.voucherNo : currentStockTaking.voucherNo}`}
              description={`Location: ${selectedLocationName} • Sub Location: ${selectedSubLocationName}`}
            />

            <Row gutter={16} className="spares-stat-row">
              <Col xs={24} xl={8}>
                <Card className="spares-stat-card">
                  <div className="spares-summary-card-title">{this.tooltipLabel("Spares Found", "Rows available from submitted SST records")}</div>
                  <div className="spares-summary-grid">
                    <div><span>Total</span><strong>{reconciliationData.sectionSummaries.sparesFound.totalItems}</strong></div>
                    <div><span>Adjusted</span><strong>{reconciliationData.sectionSummaries.sparesFound.adjusted}</strong></div>
                    <div><span>Ignored</span><strong>{reconciliationData.sectionSummaries.sparesFound.ignored}</strong></div>
                    <div><span>Pending</span><strong>{reconciliationData.sectionSummaries.sparesFound.pending}</strong></div>
                  </div>
                </Card>
              </Col>
              <Col xs={24} xl={8}>
                <Card className="spares-stat-card">
                  <div className="spares-summary-card-title">{this.tooltipLabel("Stock Taking Only", "SST quantity > 0 and inventory close quantity <= 0")}</div>
                  <div className="spares-summary-grid">
                    <div><span>Total</span><strong>{reconciliationData.sectionSummaries.stockTakingOnly.totalItems}</strong></div>
                    <div><span>Adjusted</span><strong>{reconciliationData.sectionSummaries.stockTakingOnly.adjusted}</strong></div>
                    <div><span>Ignored</span><strong>{reconciliationData.sectionSummaries.stockTakingOnly.ignored}</strong></div>
                    <div><span>Pending</span><strong>{reconciliationData.sectionSummaries.stockTakingOnly.pending}</strong></div>
                  </div>
                </Card>
              </Col>
              <Col xs={24} xl={8}>
                <Card className="spares-stat-card">
                  <div className="spares-summary-card-title">{this.tooltipLabel("Inventory Only", "Inventory closeQty is non-zero for rows outside SST, and SST rows where qty <= 0 but inventory is non-zero")}</div>
                  <div className="spares-summary-grid">
                    <div><span>Total</span><strong>{reconciliationData.sectionSummaries.inventoryOnly.totalItems}</strong></div>
                    <div><span>Adjusted</span><strong>{reconciliationData.sectionSummaries.inventoryOnly.adjusted}</strong></div>
                    <div><span>Ignored</span><strong>{reconciliationData.sectionSummaries.inventoryOnly.ignored}</strong></div>
                    <div><span>Pending</span><strong>{reconciliationData.sectionSummaries.inventoryOnly.pending}</strong></div>
                  </div>
                </Card>
              </Col>
            </Row>

            <Row gutter={16} className="spares-stat-row">
              <Col xs={24} xl={12}>
                <Card className="spares-stat-card spares-stat-card-accent">
                  <div className="spares-summary-card-title">{this.tooltipLabel("Overall Reconciliation", "Combined reconciliation progress")}</div>
                  <div className="spares-summary-grid">
                    <div><span>Total Items</span><strong>{reconciliationData.sectionSummaries.overall.totalItems}</strong></div>
                    <div><span>Total Adjusted</span><strong>{reconciliationData.sectionSummaries.overall.adjusted}</strong></div>
                    <div><span>Total Ignored</span><strong>{reconciliationData.sectionSummaries.overall.ignored}</strong></div>
                    <div>
                      <span>Total Pending</span>
                      <strong>
                        {reconciliationData.sectionSummaries.overall.pending > 0 ? (
                          <button
                            type="button"
                            className="spares-link-button"
                            onClick={this.openPendingReferenceModal}
                          >
                            {reconciliationData.sectionSummaries.overall.pending}
                          </button>
                        ) : reconciliationData.sectionSummaries.overall.pending}
                      </strong>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col xs={24} xl={12}>
                <Card className="spares-stat-card">
                  <div className="spares-summary-card-title">{this.tooltipLabel("Adjustment Value", "Valuation summary from processed adjustment rows")}</div>
                  <div className="spares-summary-grid">
                    <div><span>Vouchers</span><strong>{reconciliationData.adjustmentSummary.voucherCount}</strong></div>
                    <div><span>Added</span><strong>{this.formatCurrency(reconciliationData.adjustmentSummary.addedAmount)}</strong></div>
                    <div><span>Reduced</span><strong>{this.formatCurrency(reconciliationData.adjustmentSummary.reducedAmount)}</strong></div>
                    <div><span>Net</span><strong>{this.formatCurrency(reconciliationData.adjustmentSummary.addedAmount - reconciliationData.adjustmentSummary.reducedAmount)}</strong></div>
                  </div>
                </Card>
              </Col>
            </Row>

            <Card
              className="spares-panel-card spares-tabs-card"
              title={this.tooltipLabel("Reconciliation Sections", "Section-wise review of reconciliation output")}
              loading={loadingDetails}
            >
              <Tabs defaultActiveKey="spares-found" animated={false}>
                <TabPane tab={this.tooltipLabel(`Spares Found (${reconciliationData.sparesFound.length})`, "Records found in the selected SST")} key="spares-found">
                  <Table
                    rowKey="key"
                    rowSelection={rowSelectionConfig}
                    pagination={false}
                    dataSource={reconciliationData.sparesFound}
                    columns={[...baseColumns, actionColumn]}
                    scroll={{ x: true }}
                    locale={{ emptyText: "No spare records in selected SST." }}
                  />
                </TabPane>

                <TabPane tab={this.tooltipLabel(`Stock Taking Only (${reconciliationData.stockTakingOnly.length})`, "SST > 0 but inventory closeQty <= 0")} key="stock-taking-only">
                  <Table
                    rowKey="key"
                    rowSelection={rowSelectionConfig}
                    pagination={false}
                    dataSource={reconciliationData.stockTakingOnly}
                    columns={[...baseColumns, actionColumn]}
                    scroll={{ x: true }}
                    locale={{ emptyText: "No stock-taking-only spares for selected SST and date/location inventory." }}
                  />
                </TabPane>

                <TabPane tab={this.tooltipLabel(`Inventory Only (${reconciliationData.inventoryOnly.length})`, "Inventory closeQty is non-zero for rows outside SST, and SST rows where qty <= 0 but inventory is non-zero")} key="inventory-only">
                  <Table
                    rowKey="key"
                    rowSelection={rowSelectionConfig}
                    pagination={false}
                    dataSource={reconciliationData.inventoryOnly}
                    columns={[...baseColumns, actionColumn]}
                    scroll={{ x: true }}
                    locale={{ emptyText: "No inventory-only spares for selected SST and date/location inventory." }}
                  />
                </TabPane>

                <TabPane tab={this.tooltipLabel(`Ignored Spares (${reconciliationData.ignoredRows.length})`, "Rows marked as ignore")} key="ignored-spares">
                  <Table
                    rowKey="key"
                    pagination={false}
                    dataSource={reconciliationData.ignoredRows}
                    scroll={{ x: true }}
                    locale={{ emptyText: "No ignored spares in this reconciliation." }}
                    columns={[
                      {
                        title: this.tooltipLabel("Spare", "Ignored spare"),
                        key: "spare",
                        render: (_, record) => (
                          <div>
                            <div className="spares-cell-strong">{record.name}</div>
                            <div className="spares-cell-subtle">{record.code} • {record.category}</div>
                          </div>
                        ),
                      },
                      {
                        title: this.tooltipLabel("Difference", "Difference ignored by user"),
                        dataIndex: "delta",
                        key: "delta",
                        width: 120,
                        align: "right",
                        render: (value) => (
                          <span className={this.getDeltaClass(value)}>
                            {value > 0 ? "+" : ""}{value}
                          </span>
                        ),
                      },
                      {
                        title: this.tooltipLabel("Ignore Remark", "Captured remark"),
                        dataIndex: "ignoreRemark",
                        key: "ignoreRemark",
                        render: (value) => value || "-",
                      },
                    ]}
                  />
                </TabPane>

                <TabPane tab={this.tooltipLabel(`Adjustment Vouchers (${reconciliationData.adjustmentVouchers.length})`, "Processed adjustment entries")} key="adjustment-vouchers">
                  <Table
                    rowKey="key"
                    pagination={false}
                    dataSource={reconciliationData.adjustmentVouchers}
                    scroll={{ x: true }}
                    locale={{ emptyText: "No adjustment vouchers available for this reconciliation." }}
                    columns={[
                      {
                        title: this.tooltipLabel("Voucher", "SAV voucher number"),
                        dataIndex: "voucherNo",
                        key: "voucherNo",
                        width: 140,
                      },
                      {
                        title: this.tooltipLabel("Created On", "Processed timestamp"),
                        dataIndex: "createdAt",
                        key: "createdAt",
                        width: 160,
                        render: (value) => value ? moment(value).format("DD MMM YYYY HH:mm") : "-",
                      },
                      {
                        title: this.tooltipLabel("Spare", "Spare lines covered in voucher"),
                        key: "spare",
                        render: (_, record) => (
                          <div>
                            <div className="spares-cell-strong">{(record.spareNames || []).join(", ") || "-"}</div>
                            <div className="spares-cell-subtle">{(record.spareCodes || []).join(", ") || "-"}</div>
                          </div>
                        ),
                      },
                      {
                        title: this.tooltipLabel("Added Amount", "Total value of positive adjustments"),
                        dataIndex: "addedAmount",
                        key: "addedAmount",
                        align: "right",
                        width: 150,
                        render: (value) => this.formatCurrency(value),
                      },
                      {
                        title: this.tooltipLabel("Reduced Amount", "Total value of negative adjustments"),
                        dataIndex: "reducedAmount",
                        key: "reducedAmount",
                        align: "right",
                        width: 150,
                        render: (value) => this.formatCurrency(value),
                      },
                    ]}
                  />
                </TabPane>
              </Tabs>
              {(currentStockTaking && currentStockTaking.status === "SUBMITTED") ? (
                <div className="spares-recheck-footer">
                  <span className="spares-recheck-note">
                    Selected for bulk ignore: {selectedBulkRowsCount}. Use Recheck when all three tabs are reviewed but voucher still appears submitted.
                  </span>
                  <div className="spares-recheck-actions">
                    <Button
                      className="spares-btn spares-btn-open"
                      disabled={!selectedBulkRowsCount || bulkIgnoreSaving || !canTakeAction || recheckClosing}
                      onClick={this.openBulkIgnoreModal}
                    >
                      Ignore Selected
                    </Button>
                    <Button
                      className="spares-btn spares-btn-reconcile"
                      onClick={this.recheckAndCloseSst}
                      loading={recheckClosing}
                    >
                      Recheck & Close SST
                    </Button>
                  </div>
                </div>
              ) : null}
            </Card>
          </div>
        )}

        <Modal
          visible={!!(adjustmentModal && adjustmentModal.visible)}
          title={this.tooltipLabel("Spares Adjustment Voucher", "Prepare adjustment voucher entries from sub-location distribution.")}
          width="75%"
          wrapClassName="spares-adjustment-modal-wrap"
          onCancel={this.closeAdjustmentModal}
          onOk={this.submitAdjustmentAction}
          okText="Prepare Adjustment Voucher"
          confirmLoading={adjustmentSaving}
        >
          {adjustmentModalRow ? (
            <div className="spares-adjustment-modal">
              <div className="spares-adjustment-grid">
                <div>
                  <span>{this.tooltipLabel("SST Voucher", "Source stock-taking voucher for this adjustment.")}</span>
                  <strong>{(currentStockTaking && currentStockTaking.voucherNo) || "-"}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Spare", "Spare being adjusted.")}</span>
                  <strong>{adjustmentModalRow.code} • {adjustmentModalRow.name}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Location", "Location context from selected SST.")}</span>
                  <strong>{selectedLocationName}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Sub Location Context", "Original SST sub-location filter context.")}</span>
                  <strong>{selectedSubLocationName}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Inventory Qty", "Current inventory total from inventory report.")}</span>
                  <strong>{adjustmentModalRow.inventoryQty} {adjustmentModalRow.uom}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Stock Taking Qty", "Counted quantity in SST.")}</span>
                  <strong>{adjustmentModalRow.stockTakingQty} {adjustmentModalRow.uom}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Difference", "Stock taking minus inventory.")}</span>
                  <strong>{adjustmentModalRow.delta > 0 ? "+" : ""}{adjustmentModalRow.delta} {adjustmentModalRow.uom}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Inventory Footprint", "Sub-locations where inventory exists for this spare.")}</span>
                  <strong>{adjustmentModalRow.locationFootprint || "-"}</strong>
                </div>
              </div>

              <Card
                size="small"
                className="spares-suballocation-card"
                title={this.tooltipLabel("Sub-location Adjustment Distribution", "Distribute adjustment qty across no-sub-location and each sub-location row.")}
              >
                <div className="spares-suballocation-note">
                  Enter adjustment quantity per row. Positive quantity uses LIFO rate; negative quantity uses FIFO rate.
                </div>
                <div className="spares-suballocation-adder">
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={selectFilterByChildren}
                    value={selectedAddSubLocationId}
                    placeholder="Select other sub-location"
                    onChange={this.handleAdjustmentAddSubLocationChange}
                  >
                    {(addableSubLocations || []).map((subLocation) => (
                      <Select.Option key={subLocation._id} value={subLocation._id}>
                        {subLocation.name}
                      </Select.Option>
                    ))}
                  </Select>
                  <Button
                    style={{ marginTop: 14 }}
                    className="spares-btn spares-btn-open"
                    onClick={this.addAdjustmentSubLocationEntry}
                    disabled={!addableSubLocations.length}
                  >
                    Add Sub-location Entry
                  </Button>
                </div>
                <Alert
                  type="info"
                  showIcon
                  className="spares-rate-alert"
                  message="Rate logic"
                  description={rateSummaryText || "Increase uses LIFO and decrease uses FIFO. Provide manual rate only if LIFO is unavailable."}
                />
                <Table
                  rowKey="key"
                  size="small"
                  pagination={false}
                  scroll={{ x: 1220 }}
                  columns={adjustmentPlanColumns}
                  dataSource={adjustmentModalAllocations}
                />
              </Card>

              <div className="spares-form-field">
                <span>{this.tooltipLabel("Adjustment Reason", "Optional remark to store with all created SAV vouchers.")}</span>
                <TextArea
                  rows={3}
                  value={(adjustmentModal && adjustmentModal.reason) || ""}
                  onChange={this.handleAdjustmentReasonChange}
                  placeholder="Example: Physical verification mismatch adjusted during reconciliation."
                />
              </div>
            </div>
          ) : null}
        </Modal>

        <Modal
          visible={!!(savModal && savModal.visible)}
          title={this.tooltipLabel("SAV Details", "Reviewed adjustment voucher details for this spare")}
          width={980}
          onCancel={this.closeSavModal}
          onOk={this.closeSavModal}
          okText="Close"
          cancelButtonProps={{ style: { display: "none" } }}
        >
          {savModalRow ? (
            <div className="spares-adjustment-modal">
              <div className="spares-adjustment-grid">
                <div>
                  <span>{this.tooltipLabel("Spare", "Spare item for which SAV is created")}</span>
                  <strong>{savModalRow.code} • {savModalRow.name}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Total SAV", "Number of SAV vouchers created")}</span>
                  <strong>{toNumber(savModalRow.savCount) || ((savModalRow.savVoucherNos || []).length || (savModalRow.savVoucherNo ? 1 : 0))}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Total Adjustment Qty", "Sum of reviewed adjustment quantities")}</span>
                  <strong>{`${toNumber(savModalRow.adjustmentQuantity) > 0 ? "+" : ""}${toNumber(savModalRow.adjustmentQuantity)}`}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Applied Rate", "Effective rate based on SAV rows")}</span>
                  <strong>{toNumber(savModalRow.adjustmentRate) > 0 ? `Rs ${toNumber(savModalRow.adjustmentRate).toFixed(2)}` : "-"}</strong>
                </div>
              </div>
              <Table
                rowKey="key"
                size="small"
                pagination={false}
                scroll={{ x: true }}
                columns={savModalColumns}
                dataSource={savModalRows}
                locale={{ emptyText: "No SAV details available for this row." }}
              />
            </div>
          ) : null}
        </Modal>

        <Modal
          visible={!!(pendingRefModal && pendingRefModal.visible)}
          title={this.tooltipLabel("Pending References", "List of rows currently counted as pending in overall reconciliation")}
          width={980}
          onCancel={this.closePendingReferenceModal}
          onOk={this.closePendingReferenceModal}
          okText="Close"
          cancelButtonProps={{ style: { display: "none" } }}
        >
          <Table
            rowKey="key"
            size="small"
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
            dataSource={pendingReferenceRows}
            locale={{ emptyText: "No pending rows found." }}
            columns={[
              {
                title: this.tooltipLabel("Section", "Reconciliation tab where this row exists"),
                dataIndex: "section",
                key: "section",
                width: 150,
              },
              {
                title: this.tooltipLabel("Spare", "Spare part reference"),
                key: "spare",
                render: (_, record) => (
                  <div>
                    <div className="spares-cell-strong">{record.name}</div>
                    <div className="spares-cell-subtle">{record.code} • {record.category}</div>
                  </div>
                ),
              },
              {
                title: this.tooltipLabel("Stock Qty", "SST counted quantity"),
                dataIndex: "stockTakingQty",
                key: "stockTakingQty",
                width: 110,
                align: "right",
                render: (value) => toNumber(value).toLocaleString(),
              },
              {
                title: this.tooltipLabel("Inventory Qty", "Inventory quantity"),
                dataIndex: "inventoryQty",
                key: "inventoryQty",
                width: 120,
                align: "right",
                render: (value) => toNumber(value).toLocaleString(),
              },
              {
                title: this.tooltipLabel("Difference", "Stock taking minus inventory"),
                dataIndex: "delta",
                key: "delta",
                width: 110,
                align: "right",
                render: (value) => {
                  const deltaValue = toNumber(value);
                  return `${deltaValue > 0 ? "+" : ""}${deltaValue.toLocaleString()}`;
                },
              },
              {
                title: this.tooltipLabel("Status", "Current row status"),
                dataIndex: "status",
                key: "status",
                width: 120,
                render: (value) => value || "-",
              },
              {
                title: this.tooltipLabel("Reason", "Why this row is still counted as pending"),
                dataIndex: "reason",
                key: "reason",
                width: 260,
              },
            ]}
          />
        </Modal>

        <Modal
          visible={!!(ignoreModal && ignoreModal.visible)}
          title={ignoreModal && ignoreModal.mode === "view"
            ? this.tooltipLabel("Ignore Remark", "View the remark captured when this spare was ignored.")
            : this.tooltipLabel("Ignore Reconciliation Difference", "Provide a compulsory remark before ignoring this spare.")
          }
          onCancel={this.closeIgnoreModal}
          onOk={ignoreModal && ignoreModal.mode === "view" ? this.closeIgnoreModal : this.submitIgnoreAction}
          okText={ignoreModal && ignoreModal.mode === "view" ? "Close" : "Ignore with Remark"}
          confirmLoading={ignoreSaving}
          cancelButtonProps={ignoreModal && ignoreModal.mode === "view" ? { style: { display: "none" } } : {}}
        >
          {ignoreModal && ignoreModal.row ? (
            <div className="spares-adjustment-modal">
              <div className="spares-adjustment-grid">
                <div>
                  <span>{this.tooltipLabel("Spare", "Spare item being marked as ignored.")}</span>
                  <strong>{ignoreModal.row.code} • {ignoreModal.row.name}</strong>
                </div>
                <div>
                  <span>{this.tooltipLabel("Difference", "Difference accepted without adjustment.")}</span>
                  <strong>{ignoreModal.row.delta > 0 ? "+" : ""}{ignoreModal.row.delta} {ignoreModal.row.uom}</strong>
                </div>
              </div>
              <div className="spares-form-field">
                <span>{this.tooltipLabel("Ignore Remark", "Reason for ignoring this spare reconciliation variance.")}</span>
                <TextArea
                  rows={4}
                  value={ignoreModal.remark || ""}
                  onChange={this.handleIgnoreRemarkChange}
                  disabled={ignoreModal.mode === "view"}
                  placeholder="Example: Quantity difference accepted after physical verification."
                />
              </div>
            </div>
          ) : null}
        </Modal>

        <Modal
          visible={!!(bulkIgnoreModal && bulkIgnoreModal.visible)}
          title={this.tooltipLabel("Bulk Ignore Selected", "Ignore all selected non-reviewed spares with a single remark.")}
          onCancel={this.closeBulkIgnoreModal}
          onOk={this.submitBulkIgnoreAction}
          okText="Ignore Selected"
          confirmLoading={bulkIgnoreSaving}
        >
          <div className="spares-adjustment-modal">
            <div className="spares-bulk-ignore-summary">
              Selected rows: <strong>{Array.isArray(bulkIgnoreModal && bulkIgnoreModal.rows) ? bulkIgnoreModal.rows.length : 0}</strong>
            </div>
            <div className="spares-form-field">
              <span>{this.tooltipLabel("Ignore Remark", "This remark will be applied to all selected rows.")}</span>
              <TextArea
                rows={4}
                value={(bulkIgnoreModal && bulkIgnoreModal.remark) || ""}
                onChange={this.handleBulkIgnoreRemarkChange}
                placeholder="Example: Physical verification completed and variance accepted for selected spares."
              />
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = ({ auth }) => ({
  authUser: auth.authUser,
});

export default connect(mapStateToProps, {})(SparesReconciliation);
